import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import imageCompression from 'browser-image-compression';
import { DEFAULT_CATEGORIES_TO_SYNC, PRODUCT_CATEGORY_GROUPS } from '../../lib/categoryConfig';
import { FiGrid, FiUsers, FiLayers, FiBox, FiShoppingCart, FiShoppingBag, FiLogOut, FiMessageSquare } from 'react-icons/fi';
import { slugify } from '../../lib/productService';
import { getSiteProductsForSync } from '../../lib/syncProductsFromSite';
import Pagination from '../../components/Pagination';
import './Dashboard.css';
import logo from '../../assets/logo.png';

const VALID_TABS = ['overview', 'users', 'categories', 'products', 'orders', 'offline-sales', 'messages'];

export default function Dashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview';
    const [activeTab, setActiveTab] = useState(initialTab);
    const { signOut } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && VALID_TABS.includes(tab)) setActiveTab(tab);
    }, [searchParams]);

    const switchTab = (tab) => {
        setActiveTab(tab);
        setSearchParams(tab === 'overview' ? {} : { tab });
    };

    useEffect(() => {
        const el = document.querySelector('.dashboard-main');
        if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab]);

    const handleLogout = async () => {
        await signOut();
        navigate('/dashboard/login', { replace: true });
    };

    return (
        <div className="dashboard-container">
            <aside className="dashboard-sidebar">
                <div className="dashboard-logo-container">
                    <img src={logo} alt="Ayrine Logo" className="dashboard-logo-img" />
                </div>
                <nav className="dashboard-nav">
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => switchTab('overview')}
                    >
                        <FiGrid size={18} /> Overview
                    </button>
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => switchTab('users')}
                    >
                        <FiUsers size={18} /> Users
                    </button>
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'categories' ? 'active' : ''}`}
                        onClick={() => switchTab('categories')}
                    >
                        <FiLayers size={18} /> Categories
                    </button>
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'products' ? 'active' : ''}`}
                        onClick={() => switchTab('products')}
                    >
                        <FiBox size={18} /> Products
                    </button>
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => switchTab('orders')}
                    >
                        <FiShoppingCart size={18} /> Orders
                    </button>
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'offline-sales' ? 'active' : ''}`}
                        onClick={() => switchTab('offline-sales')}
                    >
                        <FiShoppingBag size={18} /> Offline Sales
                    </button>
                    <button
                        className={`dashboard-nav-btn ${activeTab === 'messages' ? 'active' : ''}`}
                        onClick={() => switchTab('messages')}
                    >
                        <FiMessageSquare size={18} /> Messages
                    </button>
                </nav>
                <div className="dashboard-sidebar-footer">
                    <button
                        className="dashboard-nav-btn dashboard-logout-btn"
                        onClick={handleLogout}
                    >
                        <FiLogOut size={18} /> Logout
                    </button>
                </div>
            </aside>

            <main className="dashboard-main">
                <header className="dashboard-header">
                    <h2>{activeTab === 'offline-sales' ? 'Offline Sales' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Management</h2>
                </header>

                <section className="dashboard-content">
                    {activeTab === 'overview' && <Overview />}
                    {activeTab === 'users' && <Users />}
                    {activeTab === 'categories' && <Categories />}
                    {activeTab === 'products' && <Products />}
                    {activeTab === 'orders' && <Orders />}
                    {activeTab === 'offline-sales' && <OfflineSales />}
                    {activeTab === 'messages' && <Messages />}
                </section>
            </main>
        </div>
    );
}

// ----------------------
// SUB-COMPONENTS
// ----------------------

function Overview() {
    const [stats, setStats] = useState({ users: 0, products: 0, orders: 0, categories: 0 });

    useEffect(() => {
        async function loadStats() {
            // Very basic counts
            try {
                const [
                    { count: usersCount },
                    { count: productsCount },
                    { count: ordersCount },
                    { count: categoriesCount }
                ] = await Promise.all([
                    supabase.from('users').select('*', { count: 'exact', head: true }),
                    supabase.from('products').select('*', { count: 'exact', head: true }),
                    supabase.from('orders').select('*', { count: 'exact', head: true }),
                    supabase.from('categories').select('*', { count: 'exact', head: true })
                ]);

                setStats({
                    users: usersCount || 0,
                    products: productsCount || 0,
                    orders: ordersCount || 0,
                    categories: categoriesCount || 0
                });
            } catch (err) {
                console.error("Error fetching stats:", err);
            }
        }
        loadStats();
    }, []);

    return (
        <div className="overview-cards">
            <div className="card" style={{ borderTop: '4px solid #4f46e5' }}>
                <div className="card-icon" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                    <FiUsers size={24} />
                </div>
                <div className="card-info">
                    <h3>Total Users</h3>
                    <p className="card-value">{stats.users}</p>
                </div>
            </div>
            <div className="card" style={{ borderTop: '4px solid #d97706' }}>
                <div className="card-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                    <FiLayers size={24} />
                </div>
                <div className="card-info">
                    <h3>Total Categories</h3>
                    <p className="card-value">{stats.categories}</p>
                </div>
            </div>
            <div className="card" style={{ borderTop: '4px solid #059669' }}>
                <div className="card-icon" style={{ background: '#d1fae5', color: '#059669' }}>
                    <FiBox size={24} />
                </div>
                <div className="card-info">
                    <h3>Total Products</h3>
                    <p className="card-value">{stats.products}</p>
                </div>
            </div>
            <div className="card" style={{ borderTop: '4px solid #dc2626' }}>
                <div className="card-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
                    <FiShoppingCart size={24} />
                </div>
                <div className="card-info">
                    <h3>Total Orders</h3>
                    <p className="card-value">{stats.orders}</p>
                </div>
            </div>
        </div>
    );
}

function Users() {
    const [users, setUsers] = useState([]);

    useEffect(() => {
        supabase.from('users').select('*').order('last_sign_in_at', { ascending: false }).then(({ data }) => {
            if (data) setUsers(data);
        });
    }, []);

    const formatDate = (d) => {
        if (!d) return '—';
        try {
            const dt = new Date(d);
            return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch { return '—'; }
    };

    return (
        <div className="table-container">
            <table className="dashboard-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Last Sign In</th>
                    </tr>
                </thead>
                <tbody>
                    {users.length === 0 ? (
                        <tr><td colSpan="5">No users found. Users appear here when they sign in (email or Google).</td></tr>
                    ) : (
                        users.map(u => (
                            <tr key={u.id}>
                                <td>{u.id.substring(0, 8)}...</td>
                                <td>{u.full_name || 'N/A'}</td>
                                <td>{u.email}</td>
                                <td>{u.role || 'customer'}</td>
                                <td>{formatDate(u.last_sign_in_at)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

function Categories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: '', type: 'Sub', parent_id: null });
    const [error, setError] = useState("");

    // UI states
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterMainCategory, setFilterMainCategory] = useState("All");
    const [filterSubCategory, setFilterSubCategory] = useState("All");
    const [filterSubSubCategory, setFilterSubSubCategory] = useState("All");

    const loadCategories = async () => {
        setLoading(true);
        setError("");
        try {
            const backendUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000');
            const res = await fetch(`${backendUrl}/api/categories`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Fetch failed');
            if (json.data) setCategories(json.data);
            else setCategories([]);
        } catch (err) {
            console.error('loadCategories error:', err);
            setError(err?.message || 'Unable to load categories. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    const deriveDescriptionFromTypeAndParent = (type, parentId) => {
        if (type === 'Main') return 'Main Category';
        if (type === 'Sub-sub') return 'Sub-sub category';
        if (type === 'Sub' && parentId) {
            const parent = categories.find(c => c.id === parentId || String(c.id) === String(parentId));
            if (!parent) return 'Other / Miscellaneous';
            const map = { 'Jewellery': 'Jewellery Sub-category', 'Bags & Purses': 'Bags Sub-category', 'Traditional & Western Wear': 'Clothing Sub-category' };
            return map[parent.name] || 'Other / Miscellaneous';
        }
        return 'Other / Miscellaneous';
    };

    const BACKEND_URL = process.env.NODE_ENV === 'development'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        const type = formData.type;
        const parentId = type === 'Main' ? null : (formData.parent_id || null);
        const description = deriveDescriptionFromTypeAndParent(type, parentId);

        const payload = {
            name: formData.name,
            description,
            parent_id: parentId,
            slug: slugify(formData.name),
        };

        try {
            if (formData.id) {
                const res = await fetch(`${BACKEND_URL}/api/categories/${formData.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result.error || 'Update failed');
            } else {
                const res = await fetch(`${BACKEND_URL}/api/categories/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result.error || 'Create failed');
            }

            setFormData({ id: null, name: '', type: 'Main', parent_id: null });
            await loadCategories();
            setShowAddForm(false);
        } catch (err) {
            console.error('Save category failed:', err);
            setError(err?.message || 'Save category failed. Please check fields and permissions.');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (cat) => {
        let desc = cat.description || '';
        if (desc === 'Clothing category') desc = 'Clothing Sub-category';
        if (desc === 'Other') desc = 'Other / Miscellaneous';
        if (!['Main Category', 'Jewellery Sub-category', 'Bags Sub-category', 'Clothing Sub-category', 'Other / Miscellaneous'].includes(desc) && cat.parent_id) {
            desc = 'Sub-sub category';
        }
        let type = 'Main';
        if (desc === 'Main Category') type = 'Main';
        else if (desc === 'Sub-sub category') type = 'Sub-sub';
        else type = 'Sub';
        setFormData({ id: cat.id, name: cat.name, type, parent_id: cat.parent_id || null });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) {
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${BACKEND_URL}/api/categories/${id}`, {
                method: 'DELETE',
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Delete failed');
            await loadCategories();
        } catch (err) {
            console.error('Delete category failed:', err);
            setError(err?.message || 'Delete category failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    const handleSyncCategories = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${BACKEND_URL}/api/categories/sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ categories: DEFAULT_CATEGORIES_TO_SYNC }),
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Sync failed');
            console.log('Category sync result:', result.message, result.results);
            await loadCategories();
        } catch (err) {
            console.error('Sync categories failed:', err);
            setError(err?.message || 'Sync categories failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getDepth = (category) => {
        let depth = 0;
        let current = category;
        const guard = new Set();
        while (current && current.parent_id && !guard.has(current.id)) {
            guard.add(current.id);
            const parent = categories.find((c) => c.id === current.parent_id);
            if (!parent) break;
            depth += 1;
            current = parent;
        }
        return depth;
    };

    const getCategoryDisplayInfo = (c) => {
        const desc = c?.description || '';
        const legacy = { 'Clothing category': 'Clothing Sub-category', 'Other': 'Other / Miscellaneous' };
        const normalized = legacy[desc] || desc;
        if (normalized === 'Other / Miscellaneous' && c?.parent_id) {
            const parent = categoryById[c.parent_id] || categoryById[String(c.parent_id)] || categories.find(x => x.id === c.parent_id || String(x?.id) === String(c.parent_id));
            if (parent && !parent.parent_id) return { label: 'Sub-category', badge: 'sub' };
            if (parent && parent.parent_id) return { label: 'Sub-sub category', badge: 'subsub' };
        }
        if (desc === 'Main Category') return { label: 'Main', badge: 'main' };
        if ((desc || '').includes('Sub-sub')) return { label: 'Sub-sub category', badge: 'subsub' };
        if ((desc || '').includes('Sub-category') || desc === 'Jewellery Sub-category' || desc === 'Bags Sub-category' || desc === 'Clothing Sub-category') return { label: 'Sub-category', badge: 'sub' };
        return { label: normalized, badge: 'sub' };
    };

    const categoryById = useMemo(() => {
        const m = {};
        categories.forEach(x => {
            if (x?.id) {
                m[x.id] = x;
                m[String(x.id)] = x;
            }
        });
        return m;
    }, [categories]);

    const getParent = (c) => {
        if (c.parent_id) {
            const parent = categoryById[c.parent_id] || categories.find(x => String(x?.id) === String(c.parent_id));
            return parent ? parent.name : '—';
        }
        if (c.description === 'Main Category' || c.description === 'Price Filter') return '—';
        const map = {
            'Jewellery Sub-category': 'Jewellery',
            'Clothing Sub-category': 'Traditional & Western Wear',
            'Bags Sub-category': 'Bags & Purses',
        };
        return map[c.description] || '—';
    };

    const mainCategories = categories.filter(c => c.description === 'Main Category');
    const subCategories = categories.filter(c => c.description && c.description.includes('Sub-category') && c.description !== 'Sub-sub category');

    const filterMainCats = useMemo(() => categories.filter(c => c.description === 'Main Category').sort((a, b) => a.name.localeCompare(b.name)), [categories]);
    const filterSubCategories = useMemo(() => {
        if (filterMainCategory === "All") return categories.filter(c => { if (!c.parent_id) return false; const parent = categoryById[c.parent_id] || categoryById[String(c.parent_id)]; return parent && parent.description === 'Main Category'; }).sort((a, b) => a.name.localeCompare(b.name));
        const main = categories.find(c => c.name === filterMainCategory);
        if (!main?.id) return [];
        return categories.filter(c => c.parent_id && (String(c.parent_id) === String(main.id) || c.parent_id === main.id)).sort((a, b) => a.name.localeCompare(b.name));
    }, [categories, categoryById, filterMainCategory]);
    const filterSubSubCategories = useMemo(() => {
        if (filterSubCategory === "All") return categories.filter(c => c.description === 'Sub-sub category').sort((a, b) => a.name.localeCompare(b.name));
        const sub = categories.find(c => c.name === filterSubCategory);
        if (!sub?.id) return [];
        return categories.filter(c => c.parent_id && (String(c.parent_id) === String(sub.id) || c.parent_id === sub.id)).sort((a, b) => a.name.localeCompare(b.name));
    }, [categories, filterSubCategory]);

    const isCategoryUnder = (cat, targetName) => {
        if (!cat || !targetName) return false;
        if (cat.name === targetName) return true;
        let current = cat;
        const seen = new Set();
        while (current?.parent_id && !seen.has(current.id)) {
            seen.add(current.id);
            const parent = categoryById[current.parent_id] || categoryById[String(current.parent_id)];
            if (!parent) break;
            if (parent.name === targetName) return true;
            current = parent;
        }
        return false;
    };

    const hasActiveFilters = filterMainCategory !== "All" || filterSubCategory !== "All" || filterSubSubCategory !== "All";
    const clearCategoryFilters = () => { setFilterMainCategory("All"); setFilterSubCategory("All"); setFilterSubSubCategory("All"); };

    const filteredCategories = categories.filter(c => {
        const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()));
        if (!matchesSearch) return false;
        const effective = filterSubSubCategory !== "All" ? filterSubSubCategory : filterSubCategory !== "All" ? filterSubCategory : filterMainCategory !== "All" ? filterMainCategory : null;
        if (!effective) return true;
        if (filterSubSubCategory !== "All") return c.name === filterSubSubCategory;
        return c.name === effective || isCategoryUnder(c, effective);
    });

    return (
        <div className="categories-manager">
            {error && (
                <div className="dashboard-error" role="alert">
                    {error}
                </div>
            )}
            <div className="categories-hierarchy-info">
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem' }}>Category hierarchy</h4>
                <div className="hierarchy-levels">
                    <div className="hierarchy-level" style={{ opacity: 0.7 }}>
                        <span className="hierarchy-badge main">Main</span>
                        <span>Fixed — Jewellery, Bags & Purses, Traditional & Western Wear (sync only, not creatable)</span>
                    </div>
                    <div className="hierarchy-level">
                        <span className="hierarchy-badge sub">Sub</span>
                        <span>Under Main — Necklaces, Earrings under Jewellery; Everyday Essentials under Bags</span>
                    </div>
                    <div className="hierarchy-level">
                        <span className="hierarchy-badge subsub">Sub-sub</span>
                        <span>Under Sub — Pendant Necklaces under Necklaces; Hoops, Studs under Earrings</span>
                    </div>
                </div>
            </div>
            <div className="flex-between mb-4 action-bar">
                <div className="filter-group product-filters" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="filter-item">
                        <label className="filter-label">Search</label>
                        <input
                            type="text"
                            className="modern-input"
                            placeholder="Search categories..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ minWidth: '180px' }}
                        />
                    </div>
                    <div className="filter-item">
                        <label htmlFor="cat-filter-main" className="filter-label">Main</label>
                        <select
                            id="cat-filter-main"
                            className="modern-select"
                            value={filterMainCategory}
                            onChange={(e) => { setFilterMainCategory(e.target.value); setFilterSubCategory("All"); setFilterSubSubCategory("All"); }}
                            style={{ minWidth: '140px' }}
                        >
                            <option value="All">All</option>
                            {filterMainCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="cat-filter-sub" className="filter-label">Sub</label>
                        <select
                            id="cat-filter-sub"
                            className="modern-select"
                            value={filterSubCategory}
                            onChange={(e) => { setFilterSubCategory(e.target.value); setFilterSubSubCategory("All"); }}
                            style={{ minWidth: '140px' }}
                        >
                            <option value="All">All</option>
                            {filterSubCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="cat-filter-subsub" className="filter-label">Sub-sub</label>
                        <select
                            id="cat-filter-subsub"
                            className="modern-select"
                            value={filterSubSubCategory}
                            onChange={(e) => setFilterSubSubCategory(e.target.value)}
                            style={{ minWidth: '160px' }}
                        >
                            <option value="All">All</option>
                            {filterSubSubCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    {hasActiveFilters && (
                        <button type="button" className="del-btn modern-del-btn" onClick={clearCategoryFilters} style={{ alignSelf: 'flex-end', marginBottom: '2px' }}>
                            Clear filters
                        </button>
                    )}
                </div>
                <div className="action-buttons-group">
                    <button className="action-btn" onClick={() => {
                        if (showAddForm && formData.id) setFormData({ id: null, name: '', type: 'Sub', parent_id: null });
                        setShowAddForm(!showAddForm);
                    }}>
                        {showAddForm ? 'Cancel Add' : '+ Add New Category'}
                    </button>
                    <button className="action-btn sync-btn" onClick={handleSyncCategories} title="Creates Main, Sub (Necklaces, Earrings, etc.) and Sub-sub categories if missing" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                        <span className="sync-icon">⟳</span> Sync Standard Categories
                    </button>
                </div>
            </div>

            {showAddForm && (
                <form onSubmit={handleSubmit} className="categories-form mb-4">
                    <h4 className="categories-form-title">
                        {formData.id ? 'Edit Category' : 'Create New Category'}
                    </h4>

                    <div className="level-visual-guide">
                        <div className="level-step" style={{ opacity: 0.6 }}>
                            <span className="level-num">1</span>
                            <span className="level-label">Main Category</span>
                            <span className="level-desc">Fixed — Jewellery, Bags & Purses, Traditional & Western Wear (not editable)</span>
                        </div>
                        <div className="level-arrow">↓</div>
                        <div className="level-step">
                            <span className="level-num">2</span>
                            <span className="level-label">Sub-category</span>
                            <span className="level-desc">Under a Main — Necklaces under Jewellery, Everyday Essentials under Bags</span>
                        </div>
                        <div className="level-arrow">↓</div>
                        <div className="level-step">
                            <span className="level-num">3</span>
                            <span className="level-label">Sub-sub category</span>
                            <span className="level-desc">Under a Sub — Pendant Necklaces under Necklaces, Hoops under Earrings</span>
                        </div>
                    </div>

                    <div className="categories-form-grid">
                        <div className="form-field">
                            <label className="filter-label">1. Category Name *</label>
                            <input
                                type="text"
                                className="modern-input"
                                placeholder={formData.type === 'Sub' ? 'e.g. Necklaces, Tote Bags, Sarees' : 'e.g. Pendant Necklaces, Clutch Bags, Silk Sarees'}
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-field">
                            <label className="filter-label">2. Category Type *</label>
                            {formData.id && formData.type === 'Main' ? (
                                <div className="form-hint-box form-hint-box-muted" style={{ padding: '10px 14px' }}>
                                    Main Category — fixed (Jewellery, Bags & Purses, Traditional & Western Wear). Main categories cannot be created or changed here.
                                </div>
                            ) : (
                                <select
                                    className="modern-select level-select"
                                    value={formData.type}
                                    onChange={e => setFormData(prev => ({
                                        ...prev,
                                        type: e.target.value,
                                        parent_id: e.target.value === 'Main' ? null : prev.parent_id
                                    }))}
                                    required
                                >
                                    <option value="Sub">Sub — Under a Main (e.g. Necklaces, Tote Bags, Sarees)</option>
                                    <option value="Sub-sub">Sub-sub — Under a Sub (e.g. Pendant Necklaces, Clutch Bags, Silk Sarees)</option>
                                </select>
                            )}
                        </div>

                        <div className="form-field form-field-full">
                            <label className="filter-label">3. Parent Category *</label>
                            {formData.type === 'Main' ? (
                                <div className="form-hint-box form-hint-box-muted">
                                    Main categories have no parent — they appear at the top of the hierarchy.
                                </div>
                            ) : formData.type === 'Sub' && mainCategories.length === 0 ? (
                                <div className="form-hint-box form-hint-box-muted">
                                    No Main categories yet. Create a Main category first, or run <strong>Sync Standard Categories</strong>.
                                </div>
                            ) : formData.type === 'Sub-sub' && subCategories.length === 0 ? (
                                <div className="form-hint-box form-hint-box-muted">
                                    No Sub-categories yet. Create Sub-categories (e.g. Necklaces, Earrings) under a Main category first.
                                </div>
                            ) : (
                                <>
                                    <select
                                        className="modern-select"
                                        value={formData.parent_id || ""}
                                        onChange={e => setFormData({ ...formData, parent_id: e.target.value || null })}
                                        required
                                    >
                                        <option value="" disabled>
                                            {formData.type === 'Sub' ? '— Select Main category (Jewellery, Bags & Purses, etc.) —' : '— Select Sub-category (Necklaces, Earrings, etc.) —'}
                                        </option>
                                        {formData.type === 'Sub'
                                            ? mainCategories.filter(c => !formData.id || c.id !== formData.id).sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))
                                            : subCategories.filter(c => !formData.id || c.id !== formData.id).sort((a, b) => a.name.localeCompare(b.name)).map(c => {
                                                const parent = categoryById[c.parent_id] || categoryById[String(c.parent_id)];
                                                const parentName = parent ? parent.name : '';
                                                return <option key={c.id} value={c.id}>{c.name} {parentName ? `(under ${parentName})` : ''}</option>;
                                            })
                                        }
                                    </select>
                                    {formData.type === 'Sub' && (
                                        <div className="form-hint-box">
                                            {mainCategories.length === 0
                                                ? 'Create or sync Main categories first (e.g. Jewellery, Bags & Purses).'
                                                : 'Your new category will be under the Main category you select (e.g. Necklaces under Jewellery).'}
                                        </div>
                                    )}
                                    {formData.type === 'Sub-sub' && (
                                        <div className="form-hint-box">
                                            {subCategories.length === 0
                                                ? 'Create Sub-categories first (e.g. Necklaces, Earrings) before adding Sub-sub categories.'
                                                : 'Your new category will be under the Sub-category you select (e.g. Pendant Necklaces under Necklaces).'}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {formData.id && (
                            <div className="form-field form-field-full">
                                <label className="filter-label">Current path</label>
                                <div className="category-path-display">
                                    {(() => {
                                        const current = categories.find((c) => c.id === formData.id);
                                        if (!current) return formData.name || "New category";
                                        const path = [];
                                        let node = current;
                                        const guard = new Set();
                                        while (node && !guard.has(node.id)) {
                                            guard.add(node.id);
                                            path.unshift(node.name);
                                            if (!node.parent_id) break;
                                            node = categoryById[node.parent_id] || categories.find((c) => c.id === node.parent_id);
                                        }
                                        return path.join(" → ");
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="button" className="del-btn modern-del-btn" onClick={() => {
                            setFormData({ id: null, name: '', type: 'Sub', parent_id: null });
                            setShowAddForm(false);
                        }}>Cancel</button>
                        <button
                            type="submit"
                            className="action-btn"
                            disabled={(formData.type === 'Sub' && mainCategories.length === 0) || (formData.type === 'Sub-sub' && subCategories.length === 0)}
                            title={(formData.type === 'Sub' && mainCategories.length === 0) ? 'Create Main categories first' : (formData.type === 'Sub-sub' && subCategories.length === 0) ? 'Create Sub-categories first' : ''}
                        >
                            {formData.id ? 'Update Category' : 'Create Category'}
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <p className="dashboard-loading">Loading...</p>
            ) : (
                <div className="table-container">
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Parent</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategories.length === 0 ? (
                                <tr><td colSpan="4" className="empty-state">No categories. Click Add or Sync Standard Categories.</td></tr>
                            ) : (
                                filteredCategories.map((c, idx) => (
                                    <tr key={c.id || idx} className="table-row">
                                        <td
                                            className="fixed-cell product-name-cell"
                                            style={{ paddingLeft: `${12 + getDepth(c) * 16}px` }}
                                        >
                                            {c.name}
                                        </td>
                                        <td className="fixed-cell">
                                            {(() => {
                                                const { label, badge } = getCategoryDisplayInfo(c);
                                                return (
                                                    <span className={`category-badge category-type-${badge}`}>
                                                        {label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="fixed-cell" style={{ color: '#64748b', fontSize: '0.9rem' }}>{getParent(c)}</td>
                                        <td className="fixed-cell action-cell">
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button className="action-btn" style={{ padding: '6px 14px', background: '#3b82f6' }} onClick={() => handleEdit(c)}>Edit</button>
                                                <button className="del-btn modern-del-btn" onClick={() => handleDelete(c.id)}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Map category description (from DB) to dropdown group label
const DESCRIPTION_TO_GROUP = {
    'Clothing Sub-category': 'Clothing',
    'Jewellery Sub-category': 'Jewellery',
    'Bags Sub-category': 'Bags',
    'Sub-sub category': 'Sub-sub',
    'Other / Miscellaneous': 'Other',
};

const BESTSELLER_LABEL_KEY = 'dashboard_bestseller_label';

function Products() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryMap, setCategoryMap] = useState({}); // { [name]: full category row }
    const [categoryById, setCategoryById] = useState({}); // { [id]: full category row }
    const [categoryDescriptions, setCategoryDescriptions] = useState({}); // { "CHITU": "Clothing Sub-category" }
    const [filterMainCategory, setFilterMainCategory] = useState("All");
    const [filterSubCategory, setFilterSubCategory] = useState("All");
    const [filterSubSubCategory, setFilterSubSubCategory] = useState("All");
    const [productSearchQuery, setProductSearchQuery] = useState("");
    const [priceMin, setPriceMin] = useState("");
    const [priceMax, setPriceMax] = useState("");
    const [stockFilter, setStockFilter] = useState("all"); // all | in_stock | out_of_stock
    const [bestsellerFilter, setBestsellerFilter] = useState("all"); // all | yes | no
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [bestsellerLabel, setBestsellerLabel] = useState(() => localStorage.getItem(BESTSELLER_LABEL_KEY) || 'Best Seller');
    const [productPage, setProductPage] = useState(1);
    const addFormRef = useRef(null);

    const PRODUCTS_PER_PAGE = 10;

    const BACKEND_URL = process.env.NODE_ENV === 'development'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000');

    const handleBestsellerLabelChange = (e) => {
        const val = e.target.value.trim() || 'Best Seller';
        setBestsellerLabel(val);
        localStorage.setItem(BESTSELLER_LABEL_KEY, val);
    };

    // Add Product Form State
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: '', description: '', price: '', stock_quantity: '', category: '', mainCategory: '', subCategory: '', subSubCategory: '', image_url: '', is_bestseller: false, _originalCategory: '', _originalCategoryId: null });

    const getCategoryDepth = (catName) => {
        const row = categoryMap[catName];
        if (!row) return 0;
        let depth = 0;
        let current = row;
        const guard = new Set();
        while (current && current.parent_id && !guard.has(current.id)) {
            guard.add(current.id);
            const parent = categoryById[current.parent_id];
            if (!parent) break;
            depth += 1;
            current = parent;
        }
        return depth;
    };

    const getCategoryPath = (catName) => {
        const row = categoryMap[catName];
        if (!row) return [];
        const path = [];
        let current = row;
        const guard = new Set();
        while (current && !guard.has(current.id)) {
            guard.add(current.id);
            path.unshift(current.name);
            if (!current.parent_id) break;
            current = categoryById[current.parent_id] || categoryById[String(current.parent_id)];
        }
        return path;
    };

    const mainCategories = useMemo(() => Object.values(categoryMap).filter(c => c.description === 'Main Category').sort((a, b) => a.name.localeCompare(b.name)), [categoryMap]);
    const subCategoriesForMain = useMemo(() => {
        if (!formData.mainCategory) return [];
        const main = categoryMap[formData.mainCategory];
        if (!main?.id) return [];
        return Object.values(categoryMap).filter(c => c.parent_id && (String(c.parent_id) === String(main.id) || c.parent_id === main.id)).sort((a, b) => a.name.localeCompare(b.name));
    }, [categoryMap, formData.mainCategory]);
    const subSubCategoriesForSub = useMemo(() => {
        if (!formData.subCategory) return [];
        const sub = categoryMap[formData.subCategory];
        if (!sub?.id) return [];
        return Object.values(categoryMap).filter(c => c.parent_id && (String(c.parent_id) === String(sub.id) || c.parent_id === sub.id)).sort((a, b) => a.name.localeCompare(b.name));
    }, [categoryMap, formData.subCategory]);

    const effectiveCategory = formData.subSubCategory || formData.subCategory || formData.mainCategory;

    // Filter dropdowns: cascading Main → Sub → Sub-sub
    const filterSubCategories = useMemo(() => {
        let list = [];
        if (filterMainCategory === "All") {
            list = Object.values(categoryMap).filter(c => {
                const parent = categoryById[c.parent_id] || categoryById[String(c.parent_id)];
                return c.parent_id && (parent?.description === 'Main Category' || (parent && !parent.parent_id));
            });
        } else {
            const main = categoryMap[filterMainCategory];
            if (main?.id) list = Object.values(categoryMap).filter(c => c.parent_id && (String(c.parent_id) === String(main.id) || c.parent_id === main.id));
        }
        list = [...list].sort((a, b) => a.name.localeCompare(b.name));
        // Products may use "necklace" (singular) - add to Sub dropdown if not already present
        const hasNecklace = list.some(c => c.name === "Necklaces" || c.name === "necklace");
        const productsHaveNecklace = products.some(p => (p.category || "").toLowerCase() === "necklace");
        if (!hasNecklace && productsHaveNecklace && (filterMainCategory === "All" || filterMainCategory === "Jewellery")) {
            list = [...list, { id: "legacy-necklace", name: "necklace" }].sort((a, b) => a.name.localeCompare(b.name));
        }
        return list;
    }, [categoryMap, categoryById, filterMainCategory, products]);
    const filterSubSubCategories = useMemo(() => {
        if (filterSubCategory === "All") return Object.values(categoryMap).filter(c => { if (!c.parent_id) return false; const parent = categoryById[c.parent_id] || categoryById[String(c.parent_id)]; return parent && parent.parent_id; }).sort((a, b) => a.name.localeCompare(b.name));
        const sub = categoryMap[filterSubCategory];
        if (!sub?.id) return [];
        return Object.values(categoryMap).filter(c => c.parent_id && (String(c.parent_id) === String(sub.id) || c.parent_id === sub.id)).sort((a, b) => a.name.localeCompare(b.name));
    }, [categoryMap, filterSubCategory]);

    // Product category dropdown: grouped by Main → Sub → Sub-sub categories
    const renderCategoryOptions = (catArray, isFilter = false) => {
        const list = isFilter ? catArray : catArray.filter(c => c !== "All");
        const elements = [];

        if (isFilter && list.includes("All")) {
            elements.push(<option key="All" value="All">All Categories</option>);
        }

        const allCategories = Object.values(categoryMap);
        const childrenMap = {};
        const rootNodes = [];
        const byId = {};
        allCategories.forEach(c => {
            if (c?.id) {
                byId[c.id] = c;
                byId[String(c.id)] = c;
            }
        });
        const findById = (id) => byId[id] || byId[String(id)] || allCategories.find(x => String(x?.id) === String(id));

        allCategories.forEach(c => {
            if (!c.parent_id) {
                rootNodes.push(c);
            } else {
                const pid = c.parent_id;
                if (!childrenMap[pid]) childrenMap[pid] = [];
                childrenMap[pid].push(c);
            }
        });

        const getDepth = (cat) => {
            let d = 0;
            let cur = cat;
            const seen = new Set();
            while (cur?.parent_id && !seen.has(cur.id)) {
                seen.add(cur.id);
                cur = findById(cur.parent_id);
                if (!cur) break;
                d++;
            }
            return d;
        };

        const mainCats = allCategories.filter(c => c.description === 'Main Category');
        const isSubSub = (c) => {
            if ((c.description || '') === 'Sub-sub category') return true;
            const parent = c.parent_id && (findById(c.parent_id));
            return parent && parent.parent_id;
        };
        const subSubCats = allCategories.filter(c => isSubSub(c));
        const subCats = allCategories.filter(c => c.parent_id && getDepth(c) === 1 && !isSubSub(c));

        const renderOptgroup = (label, cats) => {
            const sorted = [...cats].sort((a, b) => a.name.localeCompare(b.name));
            if (sorted.length === 0) return null;
            return (
                <optgroup key={label} label={label}>
                    {sorted.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                </optgroup>
            );
        };

        elements.push(renderOptgroup("Main Categories", mainCats));
        elements.push(renderOptgroup("Sub-categories", subCats));
        elements.push(renderOptgroup("Sub-sub categories", subSubCats));

        const dbCatNames = new Set(allCategories.map(c => c.name));
        const legacyCats = list.filter(c => c !== "All" && !dbCatNames.has(c));
        if (legacyCats.length > 0) {
            elements.push(
                <optgroup key="Legacy" label="Other / Legacy">
                    {legacyCats.map(c => <option key={c} value={c}>{c}</option>)}
                </optgroup>
            );
        }

        return elements;
    };

    // Load products from DB
    const loadProducts = async () => {
        setLoading(true);
        setError("");
        try {
            const catRes = await fetch(`${BACKEND_URL}/api/categories`);
            const catJson = await catRes.json();
            if (!catRes.ok) throw new Error(catJson.error || 'Failed to fetch categories');
            const catData = catJson.data || [];

            const { data, error: prodErr } = await supabase.from('products').select('*').order('created_at', { ascending: false }).limit(5000);
            if (prodErr) throw prodErr;

            if (data) {
                setProducts(data);
                const descMap = {};
                const map = {};
                const byId = {};
                let catsArray = [];
                if (catData) {
                    catsArray = [...catData];
                    catData.forEach(c => {
                        // Prefer the one with a parent_id if there are duplicates
                        if (!map[c.name] || c.parent_id) {
                            map[c.name] = c;
                        }
                        if (c.id) {
                            byId[c.id] = c;
                            byId[String(c.id)] = c;
                        }
                        descMap[c.name] = c.description || '';
                    });
                }
                setCategoryMap(map);
                setCategoryById(byId);
                setCategoryDescriptions(descMap);
                const dbCats = catData ? catData.map(c => c.name) : [];
                const productCats = data.map(p => p.category).filter(Boolean);
                const uniqueCats = ["All", ...new Set([...dbCats, ...productCats])];
                setCategories(uniqueCats);
            }
        } catch (err) {
            console.error('loadProducts error:', err);
            setError(err?.message || 'Unable to load products. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProducts();
    }, []);

    // Sync products FROM the website catalog (products.js) - syncs the actual site products
    const handleSyncAllProducts = async () => {
        setLoading(true);
        setError("");
        try {
            const siteProducts = getSiteProductsForSync();

            for (const p of siteProducts) {
                const categoryId = categoryMap[p.category]?.id || null;
                const { data, error: selectErr } = await supabase.from('products').select('id').eq('name', p.name);
                if (selectErr) throw selectErr;
                if (!data || data.length === 0) {
                    const slug = slugify(p.name) || `product-${Date.now()}`;
                    const { error: insertErr } = await supabase.from('products').insert([{
                        name: p.name,
                        description: p.description,
                        price: p.price,
                        stock_quantity: p.stock_quantity,
                        category: p.category,
                        category_id: categoryId,
                        slug,
                        ...(p.image_url ? { image_url: p.image_url } : {})
                    }]);
                    if (insertErr) throw insertErr;
                } else {
                    const { error: updateErr } = await supabase.from('products').update({
                        category: p.category,
                        category_id: categoryId
                    }).eq('id', data[0].id);
                    if (updateErr) throw updateErr;
                }
            }
            await loadProducts();
        } catch (err) {
            console.error('Sync site products failed:', err);
            setError(err?.message || 'Sync site products failed. Please check your permissions or try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (event, imageField = 'image_url') => {
        const imageFile = event.target.files[0];
        if (!imageFile) return;

        const options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
        };

        try {
            setError("");
            setLoading(true);
            const compressedFile = await imageCompression(imageFile, options);

            // Upload to Supabase Storage (bucket: product-images)
            const fileExt = imageFile.name.split('.').pop() || 'jpg';
            const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
            
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('product-images')
                .upload(fileName, compressedFile, { upsert: true, contentType: imageFile.type });

            if (uploadError) {
                throw new Error("Storage upload failed: " + uploadError.message);
            }

            // Get the clean public URL for the image
            const { data: { publicUrl } } = supabase.storage
                .from('product-images')
                .getPublicUrl(uploadData.path);

            setFormData(prev => ({ ...prev, [imageField]: publicUrl }));
        } catch (error) {
            console.error('Error uploading image:', error);
            setError(error?.message || 'Image upload failed. Ensure you have created the Storage bucket and configured Security Policies.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddProduct = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const slug = slugify(formData.name) || `product-${formData.id || Date.now()}`;
        const cat = effectiveCategory;
        const selectedMeta = categoryMap[cat];

        // When editing: if user didn't pick a new category, preserve the original
        const isEditing = Boolean(formData.id);
        const finalCategory = cat || (isEditing ? formData._originalCategory : '');
        const finalCategoryId = selectedMeta?.id || (isEditing && !cat ? formData._originalCategoryId : null);

        const newProduct = {
            name: formData.name,
            description: formData.description,
            price: Number(formData.price),
            stock_quantity: Number(formData.stock_quantity),
            weight: Number(formData.weight) || 0.3,
            category: finalCategory,
            category_id: finalCategoryId,
            slug,
            is_bestseller: Boolean(formData.is_bestseller),
            rating: formData.rating ? Number(formData.rating) : 0,
            review_count: formData.review_count ? Number(formData.review_count) : 0,
            ...(formData.image_url ? { image_url: formData.image_url } : {}),
            ...(formData.image_url_2 ? { image_url_2: formData.image_url_2 } : {}),
            ...(formData.image_url_3 ? { image_url_3: formData.image_url_3 } : {})
        };

        try {
            if (formData.id) {
                const { error } = await supabase.from('products').update(newProduct).eq('id', formData.id);
                if (error && error.message?.includes('slug')) {
                    const { slug: _, ...withoutSlug } = newProduct;
                    const { error: retryErr } = await supabase.from('products').update(withoutSlug).eq('id', formData.id);
                    if (retryErr) throw retryErr;
                } else if (error) {
                    throw error;
                }
                if (cat) {
                    const path = getCategoryPath(cat);
                    if (path[2]) { setFilterMainCategory(path[0] || "All"); setFilterSubCategory(path[1] || "All"); setFilterSubSubCategory(path[2]); }
                    else if (path[1]) { setFilterMainCategory(path[0] || "All"); setFilterSubCategory(path[1]); setFilterSubSubCategory("All"); }
                    else if (path[0]) { setFilterMainCategory(path[0]); setFilterSubCategory("All"); setFilterSubSubCategory("All"); }
                }
            } else {
                let { data: inserted, error } = await supabase.from('products').insert([newProduct]).select().single();
                if (error && error.message?.includes('slug')) {
                    const { slug: _, ...withoutSlug } = newProduct;
                    const res = await supabase.from('products').insert([withoutSlug]).select().single();
                    inserted = res.data;
                    error = res.error;
                }
                if (error) throw error;
                if (inserted) setProducts(prev => [inserted, ...prev]);
            }

            setFormData({ id: null, name: '', description: '', price: '', stock_quantity: '', weight: '0.3', category: '', mainCategory: '', subCategory: '', subSubCategory: '', image_url: '', image_url_2: '', image_url_3: '', is_bestseller: false, rating: '0.0', review_count: '0', _originalCategory: '', _originalCategoryId: null });
            setShowAddForm(false);
            if (cat) {
                const path = getCategoryPath(cat);
                if (path[2]) { setFilterMainCategory(path[0] || "All"); setFilterSubCategory(path[1] || "All"); setFilterSubSubCategory(path[2]); }
                else if (path[1]) { setFilterMainCategory(path[0] || "All"); setFilterSubCategory(path[1]); setFilterSubSubCategory("All"); }
                else if (path[0]) { setFilterMainCategory(path[0]); setFilterSubCategory("All"); setFilterSubSubCategory("All"); }
            }
            await loadProducts();
        } catch (err) {
            console.error('Add product failed:', err);
            setError(err?.message || 'Add product failed. Please check your permissions and required fields.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditProduct = (p) => {
        const path = getCategoryPath(p.category);
        setFormData({
            id: p.id,
            name: p.name,
            description: p.description || '',
            price: p.price,
            stock_quantity: p.stock_quantity,
            weight: p.weight || '0.3',
            category: p.category,
            mainCategory: path[0] || '',
            subCategory: path[1] || '',
            subSubCategory: path[2] || '',
            image_url: p.image_url || '',
            image_url_2: p.image_url_2 || '',
            image_url_3: p.image_url_3 || '',
            is_bestseller: Boolean(p.is_bestseller),
            rating: p.rating !== undefined && p.rating !== null ? String(p.rating) : '0.0',
            review_count: p.review_count !== undefined && p.review_count !== null ? String(p.review_count) : '0',
            _originalCategory: p.category || '',
            _originalCategoryId: p.category_id || null
        });
        setShowAddForm(true);
        setTimeout(() => {
            addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    // When filtering by category, include products in that category OR any sub-sub category under it
    const getDescendantIds = (catId) => {
        if (!catId) return new Set();
        const ids = new Set();
        const addId = (id) => { if (id) { ids.add(id); ids.add(String(id)); } };
        addId(catId);
        const childrenMap = {};
        Object.values(categoryMap).forEach(c => {
            if (c.parent_id) {
                const pid = c.parent_id;
                const arr = childrenMap[pid] || [];
                if (!arr.length) childrenMap[pid] = childrenMap[String(pid)] = arr;
                arr.push(c);
            }
        });
        const findChildren = (id) => childrenMap[id] || childrenMap[String(id)] || [];
        let queue = [catId];
        const seen = new Set();
        while (queue.length) {
            const id = queue.shift();
            const key = id || String(id);
            if (seen.has(key)) continue;
            seen.add(key);
            findChildren(id).forEach(c => { addId(c.id); queue.push(c.id); });
        }
        return ids;
    };

    const categoryFiltered = useMemo(() => {
        const effective = filterSubSubCategory !== "All" ? filterSubSubCategory : filterSubCategory !== "All" ? filterSubCategory : filterMainCategory !== "All" ? filterMainCategory : null;
        if (!effective) return products;
        const catRow = categoryMap[effective];
        if (!catRow) {
            // Match exact, or "necklace" <-> "Necklaces" (products may use singular)
            return products.filter(p => p.category === effective || (effective === "Necklaces" && p.category === "necklace") || (effective === "necklace" && p.category === "Necklaces"));
        }
        const descendantIds = getDescendantIds(catRow.id);
        const descendantNames = new Set([effective]);
        [...descendantIds].forEach(id => {
            const name = categoryById[id]?.name || categoryById[String(id)]?.name;
            if (name) descendantNames.add(name);
        });
        // Products may have "necklace" (singular) while DB uses "Necklaces"
        if (effective === "Necklaces") descendantNames.add("necklace");
        if (descendantNames.has("Necklaces")) descendantNames.add("necklace");
        return products.filter(p =>
            (p.category_id && (descendantIds.has(p.category_id) || descendantIds.has(String(p.category_id)))) ||
            (p.category && descendantNames.has(p.category))
        );
    }, [products, categoryMap, categoryById, filterMainCategory, filterSubCategory, filterSubSubCategory]);

    const filteredProducts = useMemo(() => {
        let result = categoryFiltered;
        const q = (productSearchQuery || "").trim().toLowerCase();
        if (q) {
            result = result.filter(p =>
                (p.name || "").toLowerCase().includes(q) ||
                (p.description || "").toLowerCase().includes(q) ||
                (p.category || "").toLowerCase().includes(q)
            );
        }
        const min = priceMin !== "" ? Number(priceMin) : null;
        const max = priceMax !== "" ? Number(priceMax) : null;
        if (min != null && !isNaN(min)) result = result.filter(p => (p.price ?? 0) >= min);
        if (max != null && !isNaN(max)) result = result.filter(p => (p.price ?? 0) <= max);
        if (stockFilter === "in_stock") result = result.filter(p => (p.stock_quantity ?? 0) > 0);
        if (stockFilter === "out_of_stock") result = result.filter(p => (p.stock_quantity ?? 0) <= 0);
        if (bestsellerFilter === "yes") result = result.filter(p => p.is_bestseller === true);
        if (bestsellerFilter === "no") result = result.filter(p => !p.is_bestseller);
        return result;
    }, [categoryFiltered, productSearchQuery, priceMin, priceMax, stockFilter, bestsellerFilter]);

    const totalProductPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE) || 1;
    const paginatedProducts = filteredProducts.slice(
        (productPage - 1) * PRODUCTS_PER_PAGE,
        productPage * PRODUCTS_PER_PAGE
    );

    useEffect(() => {
        setProductPage(1);
    }, [filterMainCategory, filterSubCategory, filterSubSubCategory, productSearchQuery, priceMin, priceMax, stockFilter, bestsellerFilter, filteredProducts.length]);

    const hasActiveFilters = productSearchQuery.trim() || priceMin !== "" || priceMax !== "" || stockFilter !== "all" || bestsellerFilter !== "all" || filterMainCategory !== "All" || filterSubCategory !== "All" || filterSubSubCategory !== "All";
    const clearFilters = () => {
        setFilterMainCategory("All");
        setFilterSubCategory("All");
        setFilterSubSubCategory("All");
        setProductSearchQuery("");
        setPriceMin("");
        setPriceMax("");
        setStockFilter("all");
        setBestsellerFilter("all");
        setProductPage(1);
    };

    return (
        <div className="products-manager">
            {error && (
                <div className="dashboard-error" role="alert">
                    {error}
                </div>
            )}
            <p className="dashboard-hint" style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
                <strong>Sync Site Products</strong> adds products from the website catalog. Assign new products to sub-categories. They appear in price pages when price ≤ band.
                {filteredProducts.length !== products.length && (
                    <span style={{ marginLeft: '8px', fontWeight: 600, color: 'var(--dash-accent-brand)' }}>
                        Showing {filteredProducts.length} of {products.length} products
                    </span>
                )}
            </p>
            <div className="flex-between mb-4 action-bar">
                <div className="filter-group product-filters" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="filter-item">
                        <label htmlFor="product-search" className="filter-label">Search</label>
                        <input
                            id="product-search"
                            type="text"
                            className="modern-input"
                            placeholder="Name, description..."
                            value={productSearchQuery}
                            onChange={(e) => setProductSearchQuery(e.target.value)}
                            style={{ minWidth: '160px' }}
                        />
                    </div>
                    <div className="filter-item">
                        <label htmlFor="filter-main" className="filter-label">Main</label>
                        <select
                            id="filter-main"
                            className="modern-select"
                            value={filterMainCategory}
                            onChange={(e) => { setFilterMainCategory(e.target.value); setFilterSubCategory("All"); setFilterSubSubCategory("All"); }}
                            style={{ minWidth: '140px' }}
                        >
                            <option value="All">All</option>
                            {mainCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="filter-sub" className="filter-label">Sub</label>
                        <select
                            id="filter-sub"
                            className="modern-select"
                            value={filterSubCategory}
                            onChange={(e) => { setFilterSubCategory(e.target.value); setFilterSubSubCategory("All"); }}
                            style={{ minWidth: '140px' }}
                        >
                            <option value="All">All</option>
                            {filterSubCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="filter-subsub" className="filter-label">Sub-sub</label>
                        <select
                            id="filter-subsub"
                            className="modern-select"
                            value={filterSubSubCategory}
                            onChange={(e) => setFilterSubSubCategory(e.target.value)}
                            style={{ minWidth: '160px' }}
                        >
                            <option value="All">All</option>
                            {filterSubSubCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="price-min" className="filter-label">Price (₹) min</label>
                        <input
                            id="price-min"
                            type="number"
                            className="modern-input"
                            placeholder="Min"
                            value={priceMin}
                            onChange={(e) => setPriceMin(e.target.value)}
                            min="0"
                            style={{ minWidth: '90px' }}
                        />
                    </div>
                    <div className="filter-item">
                        <label htmlFor="price-max" className="filter-label">Price (₹) max</label>
                        <input
                            id="price-max"
                            type="number"
                            className="modern-input"
                            placeholder="Max"
                            value={priceMax}
                            onChange={(e) => setPriceMax(e.target.value)}
                            min="0"
                            style={{ minWidth: '90px' }}
                        />
                    </div>
                    <div className="filter-item">
                        <label htmlFor="stock-filter" className="filter-label">Stock</label>
                        <select
                            id="stock-filter"
                            className="modern-select"
                            value={stockFilter}
                            onChange={(e) => setStockFilter(e.target.value)}
                            style={{ minWidth: '120px' }}
                        >
                            <option value="all">All</option>
                            <option value="in_stock">In stock</option>
                            <option value="out_of_stock">Out of stock</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="bestseller-filter" className="filter-label">Bestseller</label>
                        <select
                            id="bestseller-filter"
                            className="modern-select"
                            value={bestsellerFilter}
                            onChange={(e) => setBestsellerFilter(e.target.value)}
                            style={{ minWidth: '110px' }}
                        >
                            <option value="all">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <label htmlFor="bestseller-label" className="filter-label">Badge label</label>
                        <input
                            id="bestseller-label"
                            type="text"
                            className="modern-input"
                            value={bestsellerLabel}
                            onChange={handleBestsellerLabelChange}
                            placeholder="Best Seller"
                            style={{ minWidth: '110px' }}
                        />
                    </div>
                    {hasActiveFilters && (
                        <button type="button" className="del-btn modern-del-btn" onClick={clearFilters} style={{ alignSelf: 'flex-end', marginBottom: '2px' }}>
                            Clear filters
                        </button>
                    )}
                </div>
                <div className="action-buttons-group">
                    <button className="action-btn" onClick={() => setShowAddForm(!showAddForm)}>
                        {showAddForm ? 'Cancel Add' : '+ Add New Product'}
                    </button>
                    <button className="action-btn sync-btn" onClick={handleSyncAllProducts} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                        <span className="sync-icon">⟳</span> Sync Site Products
                    </button>
                </div>
            </div>

            {showAddForm && (
                <form ref={addFormRef} onSubmit={handleAddProduct} className="add-product-form mb-4" style={{ display: 'flex', flexDirection: 'column', gap: '20px', background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                    {/* Basic details */}
                    <div>
                        <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '1.05rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Basic Details</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Product Name *</label>
                                <input type="text" className="modern-input" placeholder="e.g. Elegant Gold Necklace" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Price (₹) *</label>
                                <input type="number" className="modern-input" placeholder="e.g. 1499" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} required min="0" />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Stock Quantity *</label>
                                <input type="number" className="modern-input" placeholder="e.g. 25" value={formData.stock_quantity} onChange={e => setFormData({ ...formData, stock_quantity: e.target.value })} required min="0" />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Weight (kg) *</label>
                                <input type="number" step="0.01" className="modern-input" placeholder="0.3" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })} required min="0.05" />
                            </div>
                        </div>
                    </div>

                    {/* Category & badges */}
                    <div>
                        <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '1.05rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Category & Badges</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Category (Main) {!formData.id && '*'}</label>
                                <select className="modern-select" value={formData.mainCategory} onChange={e => setFormData({ ...formData, mainCategory: e.target.value, subCategory: '', subSubCategory: '' })} required={!formData.id}>
                                    <option value="">Select Main Category</option>
                                    {mainCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Sub-category</label>
                                <select className={`modern-select ${!formData.mainCategory ? 'disabled-select' : ''}`} value={formData.subCategory} onChange={e => setFormData({ ...formData, subCategory: e.target.value, subSubCategory: '' })}>
                                    <option value="">{!formData.mainCategory ? "Select Main Category First" : "Select Sub-category"}</option>
                                    {subCategoriesForMain.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Sub-sub category</label>
                                <select className={`modern-select ${!formData.subCategory ? 'disabled-select' : ''}`} value={formData.subSubCategory} onChange={e => setFormData({ ...formData, subSubCategory: e.target.value })}>
                                    <option value="">{!formData.subCategory ? "Select Sub-category First" : "Select Sub-sub category"}</option>
                                    {subSubCategoriesForSub.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            {/* Show current stored category when editing and categories DB is empty */}
                            {formData.id && !effectiveCategory && formData._originalCategory && (
                                <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#64748b', background: '#f0fdf4', padding: '8px 12px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                    <strong>Current category:</strong> {formData._originalCategory}
                                    <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#16a34a' }}>(will be preserved)</span>
                                </div>
                            )}
                            {effectiveCategory && (
                                <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#64748b' }}>
                                    <strong>Assigned to:</strong> {getCategoryPath(effectiveCategory).join(" \u2192 ") || effectiveCategory}
                                </div>
                            )}
                            <div className={`form-bestseller-row ${formData.is_bestseller ? 'checked' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '10px', border: formData.is_bestseller ? '2px solid #d4af37' : '1px solid #e2e8f0', background: formData.is_bestseller ? 'rgba(212, 175, 55, 0.1)' : '#f8fafc', height: 'fit-content', gridColumn: '1 / -1' }}>
                                <input
                                    type="checkbox"
                                    id="is_bestseller"
                                    checked={formData.is_bestseller}
                                    onChange={e => setFormData({ ...formData, is_bestseller: e.target.checked })}
                                />
                                <label htmlFor="is_bestseller" className="filter-label" style={{ marginBottom: 0, fontWeight: formData.is_bestseller ? 700 : 600 }}>Mark as {bestsellerLabel}</label>
                                {formData.is_bestseller && <span className="form-bestseller-badge">✓ Marked</span>}
                            </div>
                        </div>
                    </div>

                    {/* Ratings Override */}
                    <div>
                        <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '1.05rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Ratings & Reviews (Optional overrides)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Average Rating (e.g. 4.8)</label>
                                <input type="number" step="0.1" max="5.0" min="0.0" className="modern-input" placeholder="0.0" value={formData.rating || ''} onChange={e => setFormData({ ...formData, rating: e.target.value })} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Total Review Count (e.g. 120)</label>
                                <input type="number" min="0" className="modern-input" placeholder="0" value={formData.review_count || ''} onChange={e => setFormData({ ...formData, review_count: Math.max(0, parseInt(e.target.value) || 0) })} />
                            </div>
                        </div>
                    </div>

                    {/* Media */}
                    <div>
                        <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '1.05rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Media (Up to 3 Images)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Image 1 (Main)</label>
                                <input type="file" accept="image/*" className="modern-input" style={{ padding: '7px' }} onChange={(e) => handleImageUpload(e, 'image_url')} />
                                {formData.image_url && (
                                    <div style={{ marginTop: '10px' }}>
                                        <img src={formData.image_url} alt="Preview 1" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Image 2</label>
                                <input type="file" accept="image/*" className="modern-input" style={{ padding: '7px' }} onChange={(e) => handleImageUpload(e, 'image_url_2')} />
                                {formData.image_url_2 && (
                                    <div style={{ marginTop: '10px' }}>
                                        <img src={formData.image_url_2} alt="Preview 2" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label className="filter-label">Image 3</label>
                                <input type="file" accept="image/*" className="modern-input" style={{ padding: '7px' }} onChange={(e) => handleImageUpload(e, 'image_url_3')} />
                                {formData.image_url_3 && (
                                    <div style={{ marginTop: '10px' }}>
                                        <img src={formData.image_url_3} alt="Preview 3" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <h4 style={{ margin: '0 0 10px 0', color: '#334155', fontSize: '1.05rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Description</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <textarea className="modern-input" placeholder="Product details..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} style={{ height: '80px', resize: 'vertical' }}></textarea>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                        <button type="button" className="del-btn modern-del-btn" onClick={() => {
                            setFormData({ id: null, name: '', description: '', price: '', stock_quantity: '', category: '', mainCategory: '', subCategory: '', subSubCategory: '', image_url: '', image_url_2: '', image_url_3: '', is_bestseller: false, rating: '0.0', review_count: '0', _originalCategory: '', _originalCategoryId: null });
                            setShowAddForm(false);
                        }}>Cancel</button>
                        <button type="submit" className="action-btn">{formData.id ? 'Save Changes' : 'Create Product'}</button>
                    </div>
                </form>
            )}

            {loading ? (
                <p className="dashboard-loading">Loading...</p>
            ) : (
                <div className="table-container">
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Category</th>
                                <th>Price (₹)</th>
                                <th>Stock</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.length === 0 ? (
                                <tr><td colSpan="5" className="empty-state">
                                    {products.length === 0
                                        ? 'No products in database. Click "Sync Site Products" above to add jewellery and catalog items from the website.'
                                        : hasActiveFilters
                                            ? 'No products match your filters. Try "Clear filters" or adjust your search.'
                                            : 'No products in this category. Try "All Categories" or another filter.'}
                                </td></tr>
                            ) : (
                                paginatedProducts.map((p, idx) => (
                                    <tr key={p.id || idx} className={`table-row ${p.is_bestseller ? 'row-bestseller' : ''}`}>
                                        <td className="fixed-cell product-name-cell">{p.name}</td>
                                        <td className="fixed-cell">
                                            <span className="category-badge">{p.category}</span>
                                        </td>
                                        <td className="fixed-cell price-cell">₹{p.price}</td>
                                        <td className="fixed-cell stock-cell">
                                            <span className={`stock-indicator ${p.stock_quantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                                {p.stock_quantity}
                                            </span>
                                        </td>
                                        <td className="fixed-cell action-cell" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <Link to={`/product/${p.slug || slugify(p.name) || p.id}`} target="_blank" rel="noopener noreferrer" className="action-btn" style={{ padding: '6px 14px', background: '#10b981', textDecoration: 'none', color: 'white' }}>View</Link>
                                            <button className="action-btn" style={{ padding: '6px 14px', background: '#3b82f6' }} onClick={() => handleEditProduct(p)}>Edit</button>
                                            <button className="del-btn modern-del-btn" onClick={async () => {
                                                await supabase.from('products').delete().eq('id', p.id);
                                                loadProducts();
                                            }}>Delete</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {filteredProducts.length > 0 && (
                        <div className="products-pagination-wrap">
                            <Pagination
                                currentPage={productPage}
                                totalPages={totalProductPages}
                                onPageChange={setProductPage}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Orders() {
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        supabase.from('orders').select('*, users(full_name)').order('created_at', { ascending: false }).then(({ data }) => {
            if (data) setOrders(data);
        });
    }, []);

    return (
        <div className="table-container">
            <table className="dashboard-table">
                <thead>
                    <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Total</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length === 0 ? (
                        <tr><td colSpan="4">No orders currently.</td></tr>
                    ) : (
                        orders.map(o => (
                            <tr key={o.id}>
                                <td>{o.id.substring(0, 8)}</td>
                                <td>{o.users?.full_name || 'Guest'}</td>
                                <td>₹{o.total_amount}</td>
                                <td>
                                    <span className={`status-badge ${o.status}`}>{o.status}</span>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

// Maps actual DB product.category strings → POS sub-category id
const CATEGORY_TO_POS_SUB = {
    // Earrings
    'Studs': 'earrings',
    'Hoops': 'earrings',
    'Monalisa': 'earrings',
    // Necklace
    'Pendant Necklaces': 'necklace',
    'Choker Necklaces': 'necklace',
    'American Diamond Stone Necklaces': 'necklace',
    'Matching Necklace + Earrings Sets': 'necklace',
    // Bracelets
    'Gold Bracelets': 'bracelets',
    'Cuff Bracelets': 'bracelets',
    // Bangles
    'Gold Kada': 'bangles',
    'Brass Gold Bangles': 'bangles',
    'Anti-Tarnish Gold Plated Bangles': 'bangles',
    'Unique Fancy Bangles': 'bangles',
    // Rings
    'Rings': 'rings',
};

// Which POS sub-category belongs under which POS main category
const POS_SUB_TO_MAIN = {
    'earrings': 'jewellery',
    'necklace': 'jewellery',
    'bracelets': 'jewellery',
    'bangles': 'jewellery',
    'rings': 'jewellery',
    'sarees': 'clothing',
    'kurtis': 'clothing',
    'western': 'clothing',
    'handbags': 'bags',
    'clutches': 'bags',
    'purses': 'bags',
};

const POS_CATEGORIES = [
    {
        id: 'jewellery',
        name: 'Jewellery',
        title: 'OUR JEWELLERY COLLECTION',
        subs: [
            { id: 'earrings', name: 'Earrings' },
            { id: 'necklace', name: 'Necklace' },
            { id: 'bracelets', name: 'Bracelets' },
            { id: 'bangles', name: 'Bangles' },
            { id: 'rings', name: 'Rings' }
        ]
    },
    {
        id: 'clothing',
        name: 'Traditional & Western Wear',
        title: 'OUR CLOTHING COLLECTION',
        subs: [
            { id: 'sarees', name: 'Sarees' },
            { id: 'kurtis', name: 'Kurtis' },
            { id: 'western', name: 'Western Wear' }
        ]
    },
    {
        id: 'bags',
        name: 'Bags & Purses',
        title: 'OUR BAGS COLLECTION',
        subs: [
            { id: 'handbags', name: 'Handbags' },
            { id: 'clutches', name: 'Clutches' },
            { id: 'purses', name: 'Purses' }
        ]
    }
];

const isPanProgressiveValid = (pan) => {
    if (!pan) return true;
    if (pan.length <= 5) return /^[A-Za-z]{1,5}$/.test(pan);
    if (pan.length <= 9) return /^[A-Za-z]{5}[0-9]{1,4}$/.test(pan);
    if (pan.length === 10) return /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(pan);
    return false;
};

const isGstProgressiveValid = (gst) => {
    if (!gst) return true;
    if (gst.length <= 2) return /^[0-9]{1,2}$/.test(gst);
    if (gst.length <= 7) return /^[0-9]{2}[A-Za-z]{1,5}$/.test(gst);
    if (gst.length <= 11) return /^[0-9]{2}[A-Za-z]{5}[0-9]{1,4}$/.test(gst);
    if (gst.length === 12) return /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(gst);
    if (gst.length === 13) return /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z]$/.test(gst);
    if (gst.length === 14) return /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z][Zz]$/.test(gst);
    if (gst.length === 15) return /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z][Zz][0-9A-Za-z]$/.test(gst);
    return false;
};

function OfflineSales() {
    const [products, setProducts] = useState([]);
    const [categoriesDb, setCategoriesDb] = useState([]);
    const [selectedMainCat, setSelectedMainCat] = useState(POS_CATEGORIES[0]);
    const [selectedSubCat, setSelectedSubCat] = useState("All");
    const [searchQuery, setSearchQuery] = useState('');
    const [cart, setCart] = useState([]);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [customerGst, setCustomerGst] = useState('');
    const [customerPan, setCustomerPan] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [discount, setDiscount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showInvoice, setShowInvoice] = useState(false);
    const [lastOrder, setLastOrder] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [editingOrder, setEditingOrder] = useState(null);

    useEffect(() => {
        loadProducts();
        loadCategories();
        loadRecentOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProducts = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('products').select('*').order('created_at', { ascending: false });
            if (data) setProducts(data);
        } catch (err) {
            console.error('Error loading products for POS', err);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/categories`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Fetch failed');
            if (json.data) setCategoriesDb(json.data);
            else setCategoriesDb([]);
        } catch (err) {
            console.error('Error loading categories for POS', err);
        } finally {
            setLoading(false);
        }
    };

    const loadRecentOrders = async () => {
        try {
            const { data, error } = await supabase
                .from('orders')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(200);

            if (!error && data) {
                // Parse shipping_address strings into objects so we can read POS details
                const parsedData = data.map(order => {
                    let shipAddr = order.shipping_address;
                    if (typeof shipAddr === 'string') {
                        try { shipAddr = JSON.parse(shipAddr); } catch (e) { shipAddr = {}; }
                    }
                    return { ...order, shipping_address: shipAddr };
                });

                // Filter locally to ensure we catch all offline orders whether stored in native columns or JSONB
                const offlineOrders = parsedData.filter(order => 
                    order.order_type === 'offline' || 
                    order.shipping_address?.order_type === 'offline' ||
                    (order.invoice_number && String(order.invoice_number).startsWith('AYR')) ||
                    (order.shipping_address?.invoice_number && String(order.shipping_address.invoice_number).startsWith('AYR'))
                );
                // Display the 50 most recent offline orders, reversed so newest is at the bottom if requested
                setRecentOrders(offlineOrders.slice(0, 50).reverse());
            } else {
                setRecentOrders([]);
            }
        } catch (err) {
            console.error('Error loading recent offline orders', err);
        }
    };

    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                if (existing.qty >= product.stock_quantity) {
                    alert('Cannot exceed available stock');
                    return prev;
                }
                return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
            }
            if (product.stock_quantity <= 0) {
                alert('Product is out of stock');
                return prev;
            }
            return [...prev, { ...product, qty: 1 }];
        });
    };

    const updateQty = (id, newQty, maxStock) => {
        if (newQty < 1) return removeFromCart(id);
        if (newQty > maxStock) {
            alert('Cannot exceed available stock');
            return;
        }
        setCart(prev => prev.map(item => item.id === id ? { ...item, qty: newQty } : item));
    };

    const removeFromCart = (id) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    // Helper: truncate to exactly 2 decimal places (no rounding up)
    const toTwo = (val) => {
        // Use truncation (floor towards zero) so 77.619… becomes 77.61, not 78
        const factor = 100;
        return Math.floor(val * factor) / factor;
    };
    // Format a number to exactly 2 decimal places for display
    const fmt = (val) => Number(val).toFixed(2);

    const totals = useMemo(() => {
        // GST is INCLUSIVE in the product price.
        // We extract the GST from the price, so:
        //   basePrice = price / (1 + rate)
        //   gst       = price - basePrice
        //   total     = price (unchanged)

        const discountVal = Number(discount) || 0;

        let baseSubtotal = 0;  // sum of base prices (excl. GST)
        let gstTotal = 0;
        let grossSubtotal = 0; // sum of original prices (incl. GST)

        cart.forEach(item => {
            const posSubId = CATEGORY_TO_POS_SUB[item.category];
            const mainCat = posSubId ? POS_SUB_TO_MAIN[posSubId] : 'jewellery';
            // 3% for jewellery, 5% for clothing and bags
            const rate = (mainCat === 'clothing' || mainCat === 'bags') ? 0.05 : 0.03;

            const itemGross = item.price * item.qty;
            const itemBase = itemGross / (1 + rate);
            const itemGst = itemGross - itemBase;

            grossSubtotal += itemGross;
            baseSubtotal += itemBase;
            gstTotal += itemGst;
        });

        // Apply discount on gross amount
        const total = Math.max(0, grossSubtotal - discountVal);

        // Use toTwo() to truncate to 2 decimal places — no rounding up
        return {
            subtotal: toTwo(baseSubtotal),   // base price excl. GST (accurate 2 decimals)
            gross: toTwo(grossSubtotal),      // original incl. GST price
            gst: toTwo(gstTotal),             // extracted GST (accurate 2 decimals)
            total: toTwo(total),              // final payable
        };
    }, [cart, discount]);

    const handleEditOrder = async (order) => {
        if (cart.length > 0) {
            if (!window.confirm("You have items in your current cart. Loading an old order will replace them. Continue?")) return;
        }
        // Always fetch fresh from DB so shipping_address/GSTIN/PAN are up-to-date
        let freshOrder = order;
        try {
            const { data, error } = await supabase
                .from('orders')
                .select('*')
                .eq('id', order.id)
                .single();
            if (!error && data) {
                freshOrder = data;
                if (typeof freshOrder.shipping_address === 'string') {
                    try { freshOrder.shipping_address = JSON.parse(freshOrder.shipping_address); } catch (e) {}
                }
            }
        } catch (_) { /* use cached order as fallback */ }

        if (typeof freshOrder.shipping_address === 'string') {
            try { freshOrder.shipping_address = JSON.parse(freshOrder.shipping_address); } catch (e) {}
        }

        setCart(freshOrder.items || []);
        setCustomerName(freshOrder.shipping_address?.customer_name || (freshOrder.customer_name === 'Walk-in' ? '' : (freshOrder.customer_name || '')));
        setCustomerPhone(freshOrder.shipping_address?.customer_phone || freshOrder.customer_phone || '');
        setCustomerAddress(freshOrder.shipping_address?.address || '');
        setCustomerGst(freshOrder.shipping_address?.gstin || '');
        setCustomerPan(freshOrder.shipping_address?.pan || '');
        setPaymentMethod(freshOrder.shipping_address?.payment_method || freshOrder.payment_method || 'Cash');
        setDiscount(freshOrder.shipping_address?.discount || freshOrder.discount || 0);
        setEditingOrder(freshOrder);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewOrder = async (order) => {
        // Always fetch fresh from DB so invoice shows latest saved data
        let freshOrder = order;
        try {
            const { data, error } = await supabase
                .from('orders')
                .select('*')
                .eq('id', order.id)
                .single();
            if (!error && data) {
                freshOrder = data;
                if (typeof freshOrder.shipping_address === 'string') {
                    try { freshOrder.shipping_address = JSON.parse(freshOrder.shipping_address); } catch (e) {}
                }
            }
        } catch (_) { /* use cached order as fallback */ }

        if (typeof freshOrder.shipping_address === 'string') {
            try { freshOrder.shipping_address = JSON.parse(freshOrder.shipping_address); } catch (e) {}
        }

        setLastOrder(freshOrder);
        setShowInvoice(true);
    };

    const handleDeleteOrder = async (orderId, skipConfirm = false) => {
        if (!skipConfirm && !window.confirm("Are you sure you want to delete this order? This will permanently remove it and restore inventory.")) return;

        setLoading(true);
        try {
            const { data: order } = await supabase.from('orders').select('*').eq('id', orderId).single();
            if (order && order.items && Array.isArray(order.items)) {
                for (const item of order.items) {
                    const { data: currentProduct } = await supabase.from('products').select('stock_quantity').eq('id', item.id).single();
                    if (currentProduct) {
                        await supabase.from('products').update({ stock_quantity: currentProduct.stock_quantity + (item.qty || 0) }).eq('id', item.id);
                    }
                }
            }

            await supabase.from('orders').delete().eq('id', orderId);
            if (editingOrder?.id === orderId) setEditingOrder(null);

            loadRecentOrders();
            loadProducts();
            if (!skipConfirm) alert('Order deleted successfully');
        } catch (err) {
            console.error('Delete error', err);
            alert('Failed to delete order');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrder = async () => {
        if (cart.length === 0) return alert('Cart is empty');
        if (!customerName || customerName.trim() === '') return alert('Customer Name is mandatory for offline sales.');
        if (!customerPhone || customerPhone.trim() === '') return alert('Phone Number is mandatory for offline sales.');

        if (customerPan && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/.test(customerPan)) return alert('Invalid PAN format (e.g., ABCDE1234F).');
        if (customerGst && !/^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/.test(customerGst)) return alert('Invalid GSTIN format (e.g., 36ABCDE1234F1Z5).');

        setLoading(true);

        let invoiceNumber = `AYR/IN/${Date.now().toString().slice(-4)}`;

        try {
            // Fetch all orders to comprehensively calculate the highest sequence ID
            const { data: allOrders, error: latestErr } = await supabase
                .from('orders')
                .select('invoice_number, shipping_address');

            let nextSeq = 1;
            let maxSeq = 0;

            if (!latestErr && allOrders) {
                for (const o of allOrders) {
                    let inv = o.invoice_number;
                    if (!inv) {
                        let sa = o.shipping_address;
                        if (typeof sa === 'string') {
                            try { sa = JSON.parse(sa); } catch (e) { sa = {}; }
                        }
                        inv = sa?.invoice_number;
                    }
                    
                    if (inv && String(inv).startsWith('AYR')) {
                        const match = String(inv).match(/AYR\/(?:IN\/)?(\d+)/) || String(inv).match(/AYR-(\d+)/);
                        if (match && match[1]) {
                            const seq = parseInt(match[1], 10);
                            if (seq > maxSeq) maxSeq = seq;
                        }
                    }
                }
                nextSeq = maxSeq + 1;
            }

            invoiceNumber = `AYR/IN/${String(nextSeq).padStart(3, '0')}`;
        } catch (e) {
            console.warn("Could not calculate sequential ID, using timestamp", e);
        }

        if (editingOrder) invoiceNumber = editingOrder.shipping_address?.invoice_number || editingOrder.invoice_number || invoiceNumber;

        try {
            if (editingOrder) {
                // Restore original stock levels for the edited order before deducting new values
                for (const oldItem of (editingOrder.items || [])) {
                    const { data: cp } = await supabase.from('products').select('stock_quantity').eq('id', oldItem.id).single();
                    if (cp) {
                        await supabase.from('products').update({ stock_quantity: cp.stock_quantity + (oldItem.qty || 0) }).eq('id', oldItem.id);
                    }
                }
            }

            // First, decrement stock independently using manual update since RPC might not be available
            for (const item of cart) {
                const { data: currentProduct } = await supabase
                    .from('products')
                    .select('stock_quantity')
                    .eq('id', item.id)
                    .single();

                if (currentProduct) {
                    const newStock = Math.max(0, currentProduct.stock_quantity - item.qty);
                    await supabase
                        .from('products')
                        .update({ stock_quantity: newStock })
                        .eq('id', item.id);
                }
            }

            const orderItems = cart.map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                qty: item.qty,
                img: item.image_url
            }));

            // ─── Build local full orderData (used for invoice display) ───
            const shippingPayload = {
                address: customerAddress?.trim() || "",
                gstin: customerGst?.trim() || "",
                pan: customerPan?.trim() || "",
                customer_name: customerName.trim(),
                customer_phone: customerPhone.trim(),
                payment_method: paymentMethod,
                invoice_number: invoiceNumber,
                discount: discount || 0,
                gst_amount: totals.gst,
                order_type: 'offline'
            };

            const orderData = {
                invoice_number: invoiceNumber,
                customer_name: customerName.trim(),
                customer_phone: customerPhone.trim(),
                total_amount: totals.total,
                status: 'delivered',
                payment_method: paymentMethod,
                order_type: 'offline',
                discount: discount || 0,
                gst_amount: totals.gst,
                items: orderItems,
                user_id: null,
                shipping_address: shippingPayload
            };

            // ─── STEP 1: Save with guaranteed-to-exist schema columns ───
            // Mirrors the proven pattern in Checkout.jsx — only columns confirmed in the schema
            const corePayload = {
                user_id: null,
                total_amount: totals.total,
                status: 'delivered',
                items: orderItems,
                shipping_address: shippingPayload   // ← JSONB column confirmed to exist
            };

            let savedRecord = null;

            if (editingOrder) {
                const { data: d, error: e } = await supabase
                    .from('orders')
                    .update(corePayload)
                    .eq('id', editingOrder.id)
                    .select()
                    .single();
                if (e) {
                    console.error("Core update failed:", e);
                } else {
                    savedRecord = d;
                }
            } else {
                const { data: d, error: e } = await supabase
                    .from('orders')
                    .insert([corePayload])
                    .select()
                    .single();
                if (e) {
                    console.error("Core insert failed:", e);
                } else {
                    savedRecord = d;
                }
            }

            // NOTE: Batch 1 and Batch 2 separated patch updates are removed 
            // since unsupported columns result in 400 Bad Request. 
            // All POS fields are packed into the shipping_address JSONB column above.

            // Always use local orderData for invoice display so no field is ever missing
            setLastOrder(savedRecord
                ? { ...orderData, id: savedRecord.id, created_at: savedRecord.created_at }
                : orderData
            );

            setShowInvoice(true);
            setCart([]);
            setCustomerName('');
            setCustomerPhone('');
            setCustomerAddress('');
            setCustomerGst('');
            setCustomerPan('');
            setDiscount(0);
            setEditingOrder(null);
            loadProducts(); // refresh stock
            await loadRecentOrders(); // await to ensure DB write is picked up

        } catch (err) {
            console.error('Error checking out:', err);
            alert('Failed to create order');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPDF = () => {
        window.print();
    };

    const categoryTree = useMemo(() => {
        return {
            mains: POS_CATEGORIES,
            subs: (mainId) => POS_CATEGORIES.find(m => m.id === mainId)?.subs || []
        };
    }, []);

    const activeSubCats = useMemo(() => {
        if (!selectedMainCat || selectedMainCat === "All") return [];
        return categoryTree.subs(selectedMainCat.id);
    }, [selectedMainCat, categoryTree]);

    const filteredProducts = useMemo(() => {
        let result = products;

        if (selectedMainCat && selectedMainCat !== "All") {
            if (selectedSubCat && selectedSubCat !== "All") {
                // Filter by specific sub-category
                result = result.filter(p => {
                    const posSubId = CATEGORY_TO_POS_SUB[p.category];
                    return posSubId === selectedSubCat.id;
                });
            } else {
                // Filter by main category — show all products whose sub belongs to this main
                result = result.filter(p => {
                    const posSubId = CATEGORY_TO_POS_SUB[p.category];
                    if (!posSubId) return false;
                    return POS_SUB_TO_MAIN[posSubId] === selectedMainCat.id;
                });
            }
        }

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p =>
                (p.name && p.name.toLowerCase().includes(q)) ||
                (p.id && String(p.id).toLowerCase().includes(q))
            );
        }

        return result;
    }, [products, selectedMainCat, selectedSubCat, searchQuery]);

    return (
        <div className="offline-sales-container">
            {/* The primary application view that will be hidden during print */}
            <div className="no-print">
                <div className="offline-sales-grid">
                    {/* Left Panel: Products */}
                    <div className="pos-products-panel">
                        {/* Category Selector */}
                        <div className="pos-categories-container mb-4">
                            <div className="pos-main-tabs">
                                {categoryTree.mains.map(cat => (
                                    <button
                                        key={cat.id}
                                        className={`pos-main-tab ${selectedMainCat?.id === cat.id ? 'active' : ''}`}
                                        onClick={() => { setSelectedMainCat(cat); setSelectedSubCat("All"); }}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>

                            {selectedMainCat && selectedMainCat !== "All" && (
                                <div className="pos-sub-pills-container">
                                    <h3 className="pos-sub-title">{selectedMainCat.title}</h3>
                                    <div className="pos-sub-pills">
                                        <button
                                            className={`pos-pill-new ${(!selectedSubCat || selectedSubCat === "All") ? 'active' : ''}`}
                                            onClick={() => setSelectedSubCat("All")}
                                        >
                                            All
                                        </button>
                                        {activeSubCats.map(sub => (
                                            <button
                                                key={sub.id}
                                                className={`pos-pill-new ${selectedSubCat?.id === sub.id ? 'active' : ''}`}
                                                onClick={() => setSelectedSubCat(sub)}
                                            >
                                                {sub.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Search Bar */}
                        <div className="pos-search-bar">
                            <span className="pos-search-icon">🔍</span>
                            <input
                                type="text"
                                className="modern-input pos-search-input"
                                placeholder="Search products by name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button className="pos-search-clear" onClick={() => setSearchQuery('')}>×</button>
                            )}
                        </div>

                        <div className="offline-product-grid">
                            {filteredProducts.map(p => (
                                <div key={p.id} className={`offline-product-card ${p.stock_quantity <= 0 ? 'out-of-stock-card' : ''}`} onClick={() => addToCart(p)}>
                                    <div className="prod-img">
                                        <img src={p.image_url || 'https://via.placeholder.com/150'} alt={p.name} loading="lazy" />
                                    </div>
                                    <div className="prod-info">
                                        <div className="prod-name">{p.name}</div>
                                        <div className="prod-meta">
                                            <span className="prod-price">₹{p.price}</span>
                                            <span className={`stock-indicator ${p.stock_quantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                                {p.stock_quantity > 0 ? `${p.stock_quantity} left` : 'Out of stock'}
                                            </span>
                                        </div>
                                    </div>
                                    {p.stock_quantity > 0 && <button className="add-btn">+</button>}
                                </div>
                            ))}
                            {filteredProducts.length === 0 && (
                                <div className="pos-empty-products">
                                    <span style={{ fontSize: '2rem' }}>📦</span>
                                    <p>No products found</p>
                                    {searchQuery && <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Try a different search term</p>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel: Cart */}
                    <div className="pos-cart-panel">
                        <h3 className="cart-title">🛒 Current Order</h3>

                        <div className="offline-cart-items">
                            {cart.length === 0 ? (
                                <div className="pos-cart-empty">
                                    <span style={{ fontSize: '2.5rem', opacity: 0.4 }}>🛍️</span>
                                    <p>Cart is empty</p>
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Click on products to add them</p>
                                </div>
                            ) : (
                                cart.map(item => (
                                    <div key={item.id} className="offline-cart-item">
                                        <div className="cart-item-info">
                                            <div className="cart-item-name">{item.name}</div>
                                            <div className="cart-item-price">₹{item.price} × {item.qty}</div>
                                        </div>
                                        <div className="cart-item-actions">
                                            <button onClick={(e) => { e.stopPropagation(); updateQty(item.id, item.qty - 1, item.stock_quantity); }}>−</button>
                                            <span>{item.qty}</span>
                                            <button onClick={(e) => { e.stopPropagation(); updateQty(item.id, item.qty + 1, item.stock_quantity); }}>+</button>
                                        </div>
                                        <button className="remove-btn" onClick={(e) => { e.stopPropagation(); removeFromCart(item.id); }}>×</button>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="cart-summary">
                            <div className="summary-row">
                                <span>Price (excl. GST)</span>
                                <span style={{ fontWeight: 600 }}>₹{fmt(totals.subtotal)}</span>
                            </div>
                            <div className="summary-row">
                                <span>GST (included)</span>
                                <span style={{ fontWeight: 600, color: 'var(--dash-accent-brand)' }}>₹{fmt(totals.gst)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Discount (₹)</span>
                                <input
                                    type="text"
                                    className="modern-input"
                                    style={{ width: '80px', padding: '4px 8px', textAlign: 'right' }}
                                    placeholder="0"
                                    value={discount === 0 ? '' : discount}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        setDiscount(val);
                                    }}
                                />
                            </div>
                            <div className="summary-row total-row">
                                <span>Total</span>
                                <span>₹{fmt(totals.total)}</span>
                            </div>
                        </div>

                        <div className="pos-customer-section">
                            <h4 className="pos-section-title">Customer Details<span style={{ color: "var(--dash-danger)", marginLeft: "4px" }}>*</span></h4>
                            <input
                                type="text"
                                className="modern-input mb-2"
                                placeholder="Customer Name"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                            />
                            <input
                                type="tel"
                                className="modern-input mb-2"
                                placeholder="Phone Number"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                            />
                            <textarea
                                className="modern-input mb-2"
                                placeholder="Complete Address (Optional)"
                                value={customerAddress}
                                onChange={(e) => setCustomerAddress(e.target.value)}
                                rows="3"
                                style={{ resize: 'vertical' }}
                            />
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        className={`modern-input ${!isGstProgressiveValid(customerGst) ? 'error' : ''}`}
                                        placeholder="GSTIN (Optional)"
                                        value={customerGst}
                                        onChange={(e) => setCustomerGst(e.target.value)}
                                        style={{ textTransform: 'uppercase', width: '100%', borderColor: !isGstProgressiveValid(customerGst) ? 'var(--dash-danger)' : undefined }}
                                    />
                                    {!isGstProgressiveValid(customerGst) && (
                                        <span style={{ color: 'var(--dash-danger)', fontSize: '11px', display: 'block', marginTop: '4px' }}>Invalid format (e.g., 36ABCDE1234F1Z5)</span>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        className={`modern-input ${!isPanProgressiveValid(customerPan) ? 'error' : ''}`}
                                        placeholder="PAN (Optional)"
                                        value={customerPan}
                                        onChange={(e) => setCustomerPan(e.target.value)}
                                        style={{ textTransform: 'uppercase', width: '100%', borderColor: !isPanProgressiveValid(customerPan) ? 'var(--dash-danger)' : undefined }}
                                    />
                                    {!isPanProgressiveValid(customerPan) && (
                                        <span style={{ color: 'var(--dash-danger)', fontSize: '11px', display: 'block', marginTop: '4px' }}>Invalid format (e.g., ABCDE1234F)</span>
                                    )}
                                </div>
                            </div>
                            <div className="pos-payment-methods">
                                {['Cash', 'UPI', 'Card'].map(method => (
                                    <button
                                        key={method}
                                        type="button"
                                        className={`pos-payment-btn ${paymentMethod === method ? 'active' : ''}`}
                                        onClick={() => setPaymentMethod(method)}
                                    >
                                        {method === 'Cash' ? '💵' : method === 'UPI' ? '📱' : '💳'} {method}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                            <button
                                className="action-btn pos-checkout-btn w-100"
                                disabled={cart.length === 0 || loading}
                                onClick={handleCreateOrder}
                            >
                                {loading ? 'Processing...' : editingOrder ? `Update Order — ₹${totals.total}` : `Complete Sale — ₹${totals.total}`}
                            </button>
                            {editingOrder && (
                                <button className="del-btn w-100" style={{ padding: '12px' }} onClick={() => {
                                    setCart([]); setCustomerName(''); setCustomerPhone(''); setCustomerAddress(''); setCustomerGst(''); setCustomerPan(''); setDiscount(0); setEditingOrder(null);
                                }}>Cancel Edit</button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Recent Orders */}
                <div className="pos-recent-orders">
                    <div className="pos-recent-header">
                        <h3>📋 Recent Offline Orders</h3>
                        <span className="pos-recent-count">{recentOrders.length} orders</span>
                    </div>
                    <div className="table-container">
                        <table className="dashboard-table">
                            <thead>
                                <tr>
                                    <th>Invoice #</th>
                                    <th>Date</th>
                                    <th>Customer</th>
                                    <th>Total</th>
                                    <th>Payment</th>
                                    <th style={{ textAlign: 'right', paddingRight: '4.5rem' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentOrders.length === 0 ? (
                                    <tr><td colSpan="6" className="empty-state">No offline orders yet. Complete a sale above to see it here.</td></tr>
                                ) : (
                                    recentOrders.map(order => (
                                        <tr key={order.id} className="table-row">
                                            <td style={{ fontWeight: 700, color: 'var(--dash-accent-brand)' }}>{order.shipping_address?.invoice_number || order.invoice_number || order.id?.substring(0, 8)}</td>
                                            <td>{new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                            <td>{order.shipping_address?.customer_name || order.customer_name || 'Walk-in'}</td>
                                            <td style={{ fontWeight: 700 }}>₹{order.total_amount}</td>
                                            <td>
                                                <span className={`pos-payment-badge ${(order.shipping_address?.payment_method || order.payment_method || 'Cash').toLowerCase()}`}>
                                                    {order.shipping_address?.payment_method || order.payment_method || 'Cash'}
                                                </span>
                                            </td>
                                            <td style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', paddingRight: '1rem' }}>
                                                <button className="action-btn" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => handleViewOrder(order)}>📄 View</button>
                                                <button className="action-btn" style={{ padding: '6px 14px', fontSize: '0.85rem', backgroundColor: '#eab308', color: '#000', borderColor: '#eab308' }} onClick={() => handleEditOrder(order)}>✏️ Edit</button>
                                                <button className="del-btn" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => handleDeleteOrder(order.id)}>🗑️</button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Print-only Invoice View Modal */}
            {showInvoice && lastOrder && (() => {
                let computedBaseSubtotal = 0;
                let computedGst = 0;
                (lastOrder.items || []).forEach(item => {
                    const posSubId = CATEGORY_TO_POS_SUB[item.category];
                    const mainCat = posSubId ? POS_SUB_TO_MAIN[posSubId] : 'jewellery';
                    const rate = (mainCat === 'clothing' || mainCat === 'bags') ? 0.05 : 0.03;
                    const itemGross = item.price * item.qty;
                    const itemBase = itemGross / (1 + rate);
                    computedBaseSubtotal += itemBase;
                    computedGst += (itemGross - itemBase);
                });

                const finalBaseSubtotal = Math.floor(computedBaseSubtotal * 100) / 100;

                // Use fallback GST amount checking the newly populated JSONB column as well
                const rawGstAmount = lastOrder.shipping_address?.gst_amount !== undefined ? lastOrder.shipping_address.gst_amount : lastOrder.gst_amount;
                const finalGst = (rawGstAmount !== undefined && rawGstAmount !== null && Number(rawGstAmount) > 0)
                    ? Number(rawGstAmount)
                    : (Math.floor(computedGst * 100) / 100);

                const finalTotal = Number(lastOrder.total_amount).toFixed(2);

                const numToWords = (num) => {
                    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
                    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                    if ((num = num.toString()).length > 9) return 'overflow';
                    const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
                    if (!n) return; let str = '';
                    str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
                    str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
                    str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
                    str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
                    str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
                    return str.trim();
                };

                const amountVal = Math.floor(Number(finalTotal));
                const paiseVal = Math.round((Number(finalTotal) - amountVal) * 100);
                let words = amountVal === 0 ? 'Zero' : numToWords(amountVal);
                if (paiseVal > 0) words += ' and ' + (numToWords(paiseVal) || 'Zero') + ' Paise';
                const amountInWords = 'Indian Rupee ' + words + ' Only';

                return (
                    <div className="offline-invoice-modal no-print-bg">
                        <div className="invoice-modal-content" style={{ maxWidth: '850px', margin: '30px auto', padding: '0', backgroundColor: '#fff', color: '#000', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                            <div className="invoice-actions no-print" style={{ padding: '15px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                                <button className="del-btn modern-del-btn" onClick={() => setShowInvoice(false)}>✕ Close</button>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button className="action-btn sync-btn" onClick={handleDownloadPDF}>📥 Save as PDF</button>
                                    <button className="action-btn" onClick={handlePrint}>🖨️ Print</button>
                                </div>
                            </div>

                            <div className="offline-invoice print-only-view" style={{ padding: '40px', fontFamily: '"Arial", sans-serif', fontSize: '13px', lineHeight: '1.4', color: '#000' }}>

                                {/* Header Section */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1.5px solid #000', paddingBottom: '15px', marginBottom: '15px' }}>
                                    <div style={{ display: 'flex', gap: '15px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img src={logo} alt="Ayrine" style={{ width: '80px', height: 'auto', objectFit: 'contain' }} />
                                        </div>
                                        <div>
                                            <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 4px 0', textTransform: 'uppercase' }}>Ayrine<br /><span style={{ fontSize: '14px', fontWeight: '600' }}>Warehouse</span></h2>
                                            <p style={{ margin: '0' }}>9-4-57/A/14, Sabera Arcade,</p>
                                            <p style={{ margin: '0' }}>Paramount Hills Gate No 2, beside Farheen Medical Shop,</p>
                                            <p style={{ margin: '0' }}>Hakeempet Kunta, Toli Chowki, Hyderabad, Telangana 500008</p>
                                            <p style={{ margin: '4px 0 0 0' }}><strong>GSTIN:</strong> 36MOBPS7776A1ZF</p>
                                            <p style={{ margin: '0' }}><strong>Phone:</strong> 9885881181</p>
                                            <p style={{ margin: '0' }}><strong>Email:</strong> ayrineluxe@gmail.com</p>
                                            <p style={{ margin: '0' }}><strong>PAN:</strong> MOBPS7776A</p>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                                        <h1 style={{ fontSize: '26px', margin: '0 0 10px 0', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>TAX INVOICE</h1>
                                    </div>
                                </div>

                                {/* Invoice Meta Row */}
                                <div style={{ display: 'flex', borderBottom: '1.5px solid #000', paddingBottom: '12px', marginBottom: '15px' }}>
                                    <div style={{ flex: '1' }}>
                                        <div style={{ display: 'flex', marginBottom: '4px' }}>
                                            <span style={{ width: '120px' }}>Invoice No</span>
                                            <span style={{ fontWeight: 'bold' }}>: {lastOrder.shipping_address?.invoice_number || lastOrder.invoice_number || lastOrder.id?.substring(0, 8)}</span>
                                        </div>
                                        <div style={{ display: 'flex' }}>
                                            <span style={{ width: '120px' }}>Invoice Date</span>
                                            <span style={{ fontWeight: 'bold' }}>: {new Date(lastOrder.created_at || Date.now()).toLocaleDateString('en-GB')}</span>
                                        </div>
                                    </div>
                                    <div style={{ flex: '1', borderLeft: '1.5px solid #000', paddingLeft: '15px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <span style={{ width: '110px' }}>Place Of Supply</span>
                                            <span style={{ fontWeight: 'bold' }}>: Hyderabad, Telangana (36)</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Block */}
                                <div style={{ display: 'flex', borderBottom: '1.5px solid #000', paddingBottom: '15px', marginBottom: '15px' }}>
                                    <div style={{ flex: '1', paddingRight: '15px' }}>
                                        <h3 style={{ fontSize: '13px', margin: '0 0 6px 0', fontWeight: 'bold', borderBottom: '1px solid #ccc', display: 'inline-block', paddingBottom: '2px' }}>Bill To</h3>
                                        <p style={{ fontWeight: 'bold', margin: '0 0 4px 0', fontSize: '14px' }}>{lastOrder.shipping_address?.customer_name || lastOrder.customer_name || 'Walk-in Customer'}</p>
                                        {lastOrder.shipping_address?.address && <p style={{ margin: '0 0 4px 0', whiteSpace: 'pre-wrap' }}>{lastOrder.shipping_address.address}</p>}
                                        {(lastOrder.shipping_address?.customer_phone || lastOrder.customer_phone) && <p style={{ margin: '0' }}>Phone: {lastOrder.shipping_address?.customer_phone || lastOrder.customer_phone}</p>}
                                        {lastOrder.shipping_address?.gstin && <p style={{ margin: '0' }}>GSTIN: {lastOrder.shipping_address.gstin.toUpperCase()}</p>}
                                        {lastOrder.shipping_address?.pan && <p style={{ margin: '0' }}>PAN: {lastOrder.shipping_address.pan.toUpperCase()}</p>}
                                        <p style={{ margin: '4px 0 0 0' }}>Payment Mode: {lastOrder.shipping_address?.payment_method || lastOrder.payment_method || 'Cash'}</p>
                                    </div>
                                    <div style={{ flex: '1', borderLeft: '1.5px solid #000', paddingLeft: '15px' }}>
                                        <h3 style={{ fontSize: '13px', margin: '0 0 6px 0', fontWeight: 'bold', borderBottom: '1px solid #ccc', display: 'inline-block', paddingBottom: '2px' }}>Ship To</h3>
                                        <p style={{ fontWeight: 'bold', margin: '0 0 4px 0', fontSize: '14px' }}>{lastOrder.shipping_address?.customer_name || lastOrder.customer_name || 'Walk-in Customer'}</p>
                                        {lastOrder.shipping_address?.address && <p style={{ margin: '0 0 4px 0', whiteSpace: 'pre-wrap' }}>{lastOrder.shipping_address.address}</p>}
                                        {(lastOrder.shipping_address?.customer_phone || lastOrder.customer_phone) && <p style={{ margin: '0' }}>Phone: {lastOrder.shipping_address?.customer_phone || lastOrder.customer_phone}</p>}
                                        {lastOrder.shipping_address?.gstin && <p style={{ margin: '0' }}>GSTIN: {lastOrder.shipping_address.gstin.toUpperCase()}</p>}
                                        {lastOrder.shipping_address?.pan && <p style={{ margin: '0' }}>PAN: {lastOrder.shipping_address.pan.toUpperCase()}</p>}
                                        <p style={{ margin: '4px 0 0 0', fontStyle: 'italic', color: '#555' }}>(Over-the-counter sale)</p>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', fontSize: '12px' }}>
                                    <thead>
                                        <tr>
                                            <th rowSpan="2" style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'center', width: '30px' }}>#</th>
                                            <th rowSpan="2" style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'left' }}>Item & Description</th>
                                            <th rowSpan="2" style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'center', width: '50px' }}>Qty</th>
                                            <th rowSpan="2" style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'right', width: '70px' }}>Rate</th>
                                            <th colSpan="2" style={{ padding: '4px', border: '1.5px solid #000', textAlign: 'center', width: '110px' }}>CGST</th>
                                            <th colSpan="2" style={{ padding: '4px', border: '1.5px solid #000', textAlign: 'center', width: '110px' }}>SGST</th>
                                            <th rowSpan="2" style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'right', width: '90px' }}>Amount</th>
                                        </tr>
                                        <tr>
                                            <th style={{ padding: '4px', border: '1.5px solid #000', textAlign: 'center', width: '50px' }}>%</th>
                                            <th style={{ padding: '4px', border: '1.5px solid #000', textAlign: 'right', width: '60px' }}>Amt</th>
                                            <th style={{ padding: '4px', border: '1.5px solid #000', textAlign: 'center', width: '50px' }}>%</th>
                                            <th style={{ padding: '4px', border: '1.5px solid #000', textAlign: 'right', width: '60px' }}>Amt</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(lastOrder.items || []).map((item, idx) => {
                                            const posSubId = CATEGORY_TO_POS_SUB[item.category];
                                            const mainCat = posSubId ? POS_SUB_TO_MAIN[posSubId] : 'jewellery';
                                            const rate = (mainCat === 'clothing' || mainCat === 'bags') ? 0.05 : 0.03;
                                            const halfRateStr = ((rate * 100) / 2).toFixed(1);

                                            const grossLine = item.price * item.qty;
                                            const baseLine = grossLine / (1 + rate);
                                            const gstAmt = grossLine - baseLine;
                                            const halfGstAmt = gstAmt / 2;

                                            return (
                                                <tr key={item.id || idx}>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'center' }}>{idx + 1}</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000' }}>{item.name}</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'center' }}>{item.qty}</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'right' }}>{baseLine.toFixed(2)}</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'center' }}>{halfRateStr}%</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'right' }}>{halfGstAmt.toFixed(2)}</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'center' }}>{halfRateStr}%</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'right' }}>{halfGstAmt.toFixed(2)}</td>
                                                    <td style={{ padding: '8px', border: '1.5px solid #000', textAlign: 'right' }}>{grossLine.toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* Bottom Info: Words, Bank & Signature */}
                                <div style={{ display: 'flex', border: '1.5px solid #000', minHeight: '180px' }}>

                                    {/* Left Split */}
                                    <div style={{ flex: '1', borderRight: '1.5px solid #000', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ padding: '10px', flex: '1' }}>
                                            <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#555' }}>Total In Words</p>
                                            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', fontStyle: 'italic' }}>{amountInWords}</p>

                                            <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#555' }}>Notes</p>
                                            <p style={{ margin: '0' }}>Thank you for shopping with Ayrine! We appreciate your business.</p>
                                        </div>
                                        <div style={{ padding: '10px', borderTop: '1.5px solid #000' }}>
                                            <p style={{ margin: '0 0 3px 0' }}><strong>Bank Name:</strong> Federal Bank</p>
                                            <p style={{ margin: '0 0 3px 0' }}><strong>Bank Account No:</strong> 24730200001083</p>
                                            <p style={{ margin: '0 0 3px 0' }}><strong>Bank IFSC:</strong> FDRL0002473</p>
                                            <p style={{ margin: '0' }}><strong>Account Holder's Name:</strong> AYRINE</p>
                                        </div>
                                    </div>

                                    {/* Right Split */}
                                    <div style={{ width: '300px', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ padding: '10px', flex: '1' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <span style={{ fontWeight: 'bold' }}>Sub Total</span>
                                                <span style={{ fontWeight: 'bold' }}>{finalBaseSubtotal.toFixed(2)}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <span>CGST Amount</span>
                                                <span>{(finalGst / 2).toFixed(2)}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <span>SGST Amount</span>
                                                <span>{(finalGst / 2).toFixed(2)}</span>
                                            </div>
                                            {Number(lastOrder.shipping_address?.discount || lastOrder.discount || 0) > 0 && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                    <span>Discount</span>
                                                    <span>-{Number(lastOrder.shipping_address?.discount || lastOrder.discount).toFixed(2)}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '10px', borderTop: '1.5px solid #000', borderBottom: '1.5px solid #000', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '14px' }}>
                                            <span>Total</span>
                                            <span>₹{finalTotal}</span>
                                        </div>
                                        {/* Signature */}
                                        <div style={{ padding: '20px 10px 10px 10px', textAlign: 'center', marginTop: 'auto', position: 'relative' }}>
                                            <img src="/signature.png" alt="Authorized Signature" style={{ width: '130px', height: 'auto', marginBottom: '-5px', mixBlendMode: 'multiply' }} />
                                            <p style={{ margin: '0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ width: '150px', borderTop: '1px solid #000', paddingTop: '4px', fontSize: '12px' }}>Authorized Signature</span>
                                            </p>
                                        </div>
                                    </div>

                                </div>

                                {/* Custom footer - replaces browser URL footer */}
                                <div style={{ marginTop: '20px', paddingTop: '10px', borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <span style={{ fontSize: '17px', fontWeight: 'bold', color: '#1a1a1a', letterSpacing: '0.5px' }}>www.ayrine.in</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

function Messages() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadMessages();
    }, []);

    const loadMessages = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('contact_messages')
            .select('*')
            .order('created_at', { ascending: false });
        if (data) setMessages(data);
        setLoading(false);
    };

    const formatDate = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="table-container" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '15px' }}>Customer Messages</h3>
            <table className="dashboard-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Name</th>
                        <th>Email & Phone</th>
                        <th>Topic & Order ID</th>
                        <th>Message</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan="5">Loading messages...</td></tr>
                    ) : messages.length === 0 ? (
                        <tr><td colSpan="5" className="empty-state">No messages found.</td></tr>
                    ) : (
                        messages.map(m => (
                            <tr key={m.id}>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(m.created_at)}</td>
                                <td><strong>{m.name}</strong></td>
                                <td>
                                    <div><a href={`mailto:${m.email}`}>{m.email}</a></div>
                                    <div style={{color: '#666', fontSize: '0.9em', marginTop: '4px'}}>{m.mobile}</div>
                                </td>
                                <td>
                                    <div style={{ textTransform: 'capitalize' }}>{m.topic}</div>
                                    {m.order_id && <div style={{ fontWeight: 'bold', fontSize: '0.9em', color: '#111', marginTop: '4px' }}>{m.order_id}</div>}
                                </td>
                                <td style={{ maxWidth: '300px', whiteSpace: 'normal', color: '#333' }}>{m.message}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
